<template>
  <v-dialog
    :model-value="store.dialogOpen"
    max-width="900"
    scrollable
    data-testid="detailed-publish-impact-dialog"
    @update:model-value="onDialogVisibilityChange"
  >
    <v-card class="detailed-publish-impact-card">
      <v-card-title class="d-flex justify-space-between align-center">
        <span class="text-h6 font-weight-bold" data-testid="detailed-publish-impact-dialog-title">
          {{ dialogTitle }}
        </span>
        <v-btn
          icon="mdi-close"
          variant="text"
          aria-label="Close"
          data-testid="detailed-publish-impact-dialog-close"
          @click="closeDialog"
        />
      </v-card-title>
      <v-card-subtitle
        v-if="dialogSubtitle"
        data-testid="detailed-publish-impact-dialog-subtitle"
      >
        {{ dialogSubtitle }}
      </v-card-subtitle>

      <v-card-text v-if="isPassThroughDialog" class="pt-4 detailed-publish-impact-body">
        <FeedPreparationPassThroughPanel
          :endpoint="props.endpoint"
          :jazel-id="props.jazelId"
          :correlation-id="props.correlationId"
          :session-token="props.sessionToken"
          :active="store.dialogOpen"
        />
      </v-card-text>

      <v-card-text v-else class="pt-4 detailed-publish-impact-body">
        <section
          v-if="
            isStageScopedDialog && (stageDetailLines.length > 0 || stageDetailActions.length > 0)
          "
          class="mb-4"
          data-testid="detailed-publish-impact-stage-details"
        >
          <div
            v-if="stageDetailSummary"
            class="text-body-2 text-medium-emphasis mb-3"
            data-testid="detailed-publish-impact-stage-detail-summary"
          >
            {{ stageDetailSummary }}
          </div>

          <div
            v-if="stageDetailActions.length > 0"
            class="d-flex flex-wrap ga-2"
            data-testid="detailed-publish-impact-stage-actions"
          >
            <v-btn
              v-for="action in stageDetailActions"
              :key="action.id"
              size="small"
              variant="tonal"
              color="primary"
              :prepend-icon="action.icon"
              :loading="action.loading"
              :disabled="action.disabled || action.loading"
              :data-testid="`detailed-publish-impact-stage-action-${action.id}`"
              @click="$emit('stage-detail-action', action.id)"
            >
              {{ action.label }}
            </v-btn>
          </div>

          <v-divider class="mt-4" />
        </section>

        <!--
          Phase 6.y scope banner — preserved for the listingRules-only
          baseline (Phase 6.y regression bar). Once the manifest
          carries a groups pointer too, the banner is suppressed
          because the dialog is no longer "publish rules only".
        -->
        <v-alert
          v-if="renderListingRulesOnlyBanner && !isListingRulesDecisionBrowse"
          type="info"
          variant="tonal"
          density="comfortable"
          class="mb-4"
          data-testid="detailed-publish-impact-scope-note"
        >
          Detailed results are currently available for publish rules only.
        </v-alert>

        <!-- ===========================
             VIN lookup section
             =========================== -->
        <section v-if="!isStageScopedDialog" data-testid="detailed-publish-impact-vin-section">
          <h3 class="text-subtitle-1 font-weight-bold mb-3">Look up a vehicle</h3>

          <!--
            Explicit @click on the submit button + @keydown.enter on the
            input keeps behavior identical across browsers and the
            jsdom-based unit tests, which do NOT synthesize a form
            submit when a button[type=submit] is clicked.
          -->
          <div class="d-flex align-center ga-3 flex-wrap">
            <v-text-field
              v-model="vinInput"
              label="VIN"
              variant="outlined"
              density="comfortable"
              hide-details
              :maxlength="vinMaxLength"
              autocomplete="off"
              :disabled="vinLookupInFlight"
              data-testid="detailed-publish-impact-vin-input"
              @keydown.enter.prevent="submitVinLookup"
            />
            <v-btn
              color="primary"
              variant="flat"
              :loading="vinLookupInFlight"
              :disabled="vinLookupDisabled"
              data-testid="detailed-publish-impact-vin-submit"
              @click="submitVinLookup"
            >
              Look up
            </v-btn>
          </div>

          <div
            v-if="store.vinTrailStatus === 'invalid'"
            class="text-caption text-error mt-2"
            data-testid="detailed-publish-impact-vin-invalid"
          >
            Enter a valid VIN.
          </div>

          <div
            v-else-if="store.vinTrailStatus === 'loading'"
            class="d-flex align-center ga-2 mt-4"
            data-testid="detailed-publish-impact-vin-loading"
          >
            <v-progress-circular indeterminate color="primary" size="20" />
            <span class="text-body-2">Looking up...</span>
          </div>

          <!--
            Adjustment #1 — dedicated alert for "VIN was not part of the
            listing-rules decisions for this run". Distinct from the
            transient `'unavailable'` state below; we deliberately do
            NOT render the per-stage list here because there are no
            records to show.
          -->
          <v-alert
            v-else-if="store.vinTrailStatus === 'notInRun'"
            type="info"
            variant="tonal"
            density="comfortable"
            class="mt-4"
            data-testid="detailed-publish-impact-vin-not-in-run"
          >
            No results found for this VIN in this publish run.
          </v-alert>

          <v-alert
            v-else-if="
              store.vinTrailStatus === 'unavailable' || store.vinTrailStatus === 'notFound'
            "
            type="info"
            variant="tonal"
            density="comfortable"
            class="mt-4"
            data-testid="detailed-publish-impact-vin-unavailable"
          >
            We couldn't load this vehicle's results right now. Please try again or come back later.
          </v-alert>

          <div
            v-else-if="store.vinTrailReady && vinTrailRows.length > 0"
            class="mt-4"
            data-testid="detailed-publish-impact-vin-trail"
          >
            <div class="text-body-2 text-medium-emphasis mb-3">
              Showing results for VIN
              <span class="font-weight-bold" data-testid="detailed-publish-impact-vin-echo">
                {{ store.vinTrailQueryEcho }} </span
              >.
            </div>
            <v-list density="comfortable" class="pa-0">
              <v-list-item
                v-for="row in vinTrailRows"
                :key="row.key"
                :data-testid="`detailed-publish-impact-vin-row-${row.stage}`"
                class="px-0"
              >
                <template #default>
                  <div>
                    <div class="text-subtitle-2 font-weight-bold">{{ row.label }}</div>
                    <!--
                      Phase 7.y / 8.y — branch on stage. Groups rows
                      render the structured Added / Removed / Reasons
                      view; fieldMapping rows render the compact
                      summary + lazy expander (option C); every other
                      stage keeps the Phase 6.y status-only label.
                    -->
                    <div v-if="row.kind === 'groups'" class="mt-1">
                      <GroupsDecisionTrailRow
                        v-for="(record, idx) in row.records"
                        :key="`${row.key}:rec:${idx}`"
                        :record="record"
                      />
                    </div>
                    <div v-else-if="row.kind === 'fieldMapping'" class="mt-1">
                      <FieldMappingDecisionTrailRow
                        v-for="(record, idx) in row.records"
                        :key="`${row.key}:rec:${idx}`"
                        :record="record"
                      />
                    </div>
                    <div v-else-if="row.kind === 'preProcessor'" class="mt-1">
                      <PreProcessorDecisionTrailRow
                        v-for="(record, idx) in row.records"
                        :key="`${row.key}:rec:${idx}`"
                        :record="record"
                      />
                    </div>
                    <div v-else-if="row.kind === 'feedFieldRules'" class="mt-1">
                      <FeedFieldRulesDecisionTrailRow
                        v-for="(record, idx) in row.records"
                        :key="`${row.key}:rec:${idx}`"
                        :record="record"
                      />
                    </div>
                    <div v-else class="text-body-2 text-medium-emphasis">
                      <span data-testid="detailed-publish-impact-vin-row-status">{{
                        row.statusLabel
                      }}</span>
                      <template v-if="row.detail">— {{ row.detail }}</template>
                    </div>
                  </div>
                </template>
              </v-list-item>
            </v-list>
          </div>
        </section>

        <v-divider v-if="!isStageScopedDialog" class="my-4" />

        <!-- ===========================
             Phase 7.y — tabbed paginated browser
             =========================== -->
        <section data-testid="detailed-publish-impact-browser-section">
          <!-- Vehicle Groups uses the dialog title only; a second heading here
               would duplicate "Vehicle Groups (N)" under the same label. -->
          <h3
            v-if="!isGroupsDecisionBrowse && !isListingRulesDecisionBrowse"
            class="text-subtitle-1 font-weight-bold mb-3"
            data-testid="detailed-publish-impact-browser-heading"
          >
            {{ browserHeadingWithCount }}
          </h3>

          <v-text-field
            v-if="isStageScopedDialog"
            v-model="stageVinSearch"
            :label="stageSearchLabel"
            variant="outlined"
            density="comfortable"
            clearable
            hide-details
            class="mb-3"
            data-testid="detailed-publish-impact-stage-vin-search"
          />

          <div
            v-if="filteredOutDetailsNote"
            class="text-caption text-medium-emphasis mb-3"
            data-testid="detailed-publish-impact-filtered-out-note"
          >
            {{ filteredOutDetailsNote }}
          </div>

          <div
            v-if="showPreProcessorDecisionFilter"
            class="d-flex flex-wrap ga-2 mb-3"
            data-testid="detailed-publish-impact-preprocessor-filter"
          >
            <v-btn
              v-for="option in preProcessorDecisionFilterOptions"
              :key="option.value"
              size="small"
              :variant="preProcessorDecisionFilter === option.value ? 'flat' : 'tonal'"
              color="primary"
              :data-testid="`detailed-publish-impact-preprocessor-filter-${option.value}`"
              @click="preProcessorDecisionFilter = option.value"
            >
              {{ option.label }}
            </v-btn>
          </div>

          <!--
            Phase 7.y — tab strip. Visible only when the manifest
            advertises MORE THAN ONE decision-level stage. Single-stage
            manifests render no tabs (the Phase 6.y look is preserved
            verbatim).
          -->
          <v-tabs
            v-if="showStageTabs"
            :model-value="store.activeBrowserStage ?? undefined"
            class="mb-3"
            data-testid="detailed-publish-impact-browser-tabs"
            @update:model-value="onTabChange"
          >
            <v-tab
              v-for="stage in store.availableBrowserStages"
              :key="stage"
              :value="stage"
              :data-testid="`detailed-publish-impact-browser-tab-${stage}`"
            >
              {{ stageTabLabel(stage) }}
            </v-tab>
          </v-tabs>

          <!-- No active stage / no manifest pointers — empty fallback. -->
          <v-alert
            v-if="!activeStage"
            type="info"
            variant="tonal"
            density="comfortable"
            data-testid="detailed-publish-impact-browser-no-stage"
          >
            No detailed publish results were captured for this run.
          </v-alert>

          <template v-else>
            <!-- Initial loading: no rows yet -->
            <div
              v-if="activeBrowserStatus === 'loading' && activeBrowserPagesLength === 0"
              class="d-flex align-center ga-2"
              data-testid="detailed-publish-impact-browser-loading"
            >
              <v-progress-circular indeterminate color="primary" size="20" />
              <span class="text-body-2">Loading results...</span>
            </div>

            <!-- Unavailable AND nothing loaded yet -->
            <v-alert
              v-else-if="activeBrowserStatus === 'unavailable' && activeBrowserPagesLength === 0"
              type="info"
              variant="tonal"
              density="comfortable"
              data-testid="detailed-publish-impact-browser-unavailable"
            >
              We couldn't load the results for this run right now.
            </v-alert>

            <!--
              OK with zero records. Groups is excluded: a changed-only browse
              that returns no rows must still render the zero-changed copy AND
              the search box (search spans all evaluated vehicles), so it falls
              through to the content block below instead of this generic empty
              alert.
            -->
            <div
              v-else-if="
                activeBrowserStatus === 'ok' &&
                store.activeBrowserRows.length === 0 &&
                !isGroupsDecisionBrowse &&
                !isListingRulesDecisionBrowse &&
                !isFeedFieldRulesDecisionBrowse
              "
              data-testid="detailed-publish-impact-browser-empty"
            >
              <v-alert type="info" variant="tonal" density="comfortable">
                No matching results found in the available data for this run.
              </v-alert>
              <!--
                Adjustment #1 — when the empty state coincides with
                parse warnings or oversized records, the dealer needs
                to know the empty result might be partial.
              -->
              <div
                v-if="store.activeBrowserHasIncompleteData"
                class="text-caption text-medium-emphasis mt-2"
                data-testid="detailed-publish-impact-browser-incomplete"
              >
                Some results may not be shown.
              </div>
            </div>

            <!--
              Rows present.

              Guardrail #6 — the table SHAPE stays unchanged:
              VIN / outcome / primary reason. Structured Added/Removed
              UI lives only in the VIN-trail dialog rows above; never
              in the browser table.
            -->
            <div
              v-else-if="
                browserRowViewModels.length > 0 ||
                isGroupsDecisionBrowse ||
                isListingRulesDecisionBrowse ||
                isFeedFieldRulesDecisionBrowse
              "
              class="detailed-publish-impact-results"
              data-testid="detailed-publish-impact-browser-table"
            >
              <!--
                Vehicle Groups — helper text and master/detail sit OUTSIDE the
                shared results scroll so the left VIN list scrolls independently
                while the right details pane stays visible. This drill-down is
                scoped to VIN-level group CHANGES from this publish; run-wide
                group population badges are intentionally not shown here.
              -->
              <template v-if="isGroupsDecisionBrowse">
                <div
                  v-if="decisionSearchStatus === 'loading'"
                  class="d-flex align-center ga-2 mb-3"
                  data-testid="detailed-publish-impact-field-mapping-search-loading"
                >
                  <v-progress-circular indeterminate color="primary" size="18" />
                  <span class="text-body-2">Searching decisions...</span>
                </div>

                <v-alert
                  v-else-if="decisionSearchStatus === 'unavailable'"
                  type="info"
                  variant="tonal"
                  density="comfortable"
                  class="mb-3"
                  data-testid="detailed-publish-impact-field-mapping-search-unavailable"
                >
                  We couldn't search decisions right now. Please try again.
                </v-alert>

                <v-alert
                  v-if="
                    decisionSearchActive &&
                    groupsBrowseRows.length === 0 &&
                    decisionSearchStatus !== 'loading' &&
                    decisionSearchStatus !== 'unavailable'
                  "
                  type="info"
                  variant="tonal"
                  density="comfortable"
                  class="mb-3"
                  data-testid="detailed-publish-impact-groups-search-empty"
                >
                  No group decisions match this search.
                </v-alert>

                <div
                  v-if="!decisionSearchActive && !showGroupsNoChangedNotice"
                  class="text-caption text-medium-emphasis mb-4"
                  data-testid="detailed-publish-impact-groups-default-helper"
                >
                  Showing vehicles whose group membership changed. Search can find any
                  evaluated VIN.
                </div>

                <v-alert
                  v-if="showGroupsNoChangedNotice"
                  type="info"
                  variant="tonal"
                  density="comfortable"
                  class="mb-3"
                  data-testid="detailed-publish-impact-groups-no-changed"
                >
                  <div>No vehicles changed groups in this run.</div>
                  <div>Search for a VIN to inspect its group assignment.</div>
                </v-alert>

                <div
                  v-if="groupsBrowseRows.length > 0"
                  class="groups-decision-layout"
                  data-testid="detailed-publish-impact-groups-decisions"
                >
                  <div
                    class="groups-vin-list"
                    data-testid="detailed-publish-impact-groups-vin-list"
                  >
                    <button
                      v-for="row in groupsBrowseRows"
                      :key="row.key"
                      type="button"
                      class="field-mapping-vin-list-row"
                      :class="{
                        'field-mapping-vin-list-row--selected': row.vin === selectedGroupsVin,
                      }"
                      :data-testid="`detailed-publish-impact-browser-row-${row.vin}`"
                      @click="selectedGroupsVin = row.vin"
                    >
                      <code class="vin-cell" data-testid="detailed-publish-impact-groups-vin">
                        {{ row.vin }}
                      </code>
                      <span
                        v-if="row.displayCurrentGroups.length > 0"
                        class="groups-badges"
                        data-testid="detailed-publish-impact-groups-vin-badges"
                      >
                        <span
                          v-for="badge in row.displayCurrentGroups"
                          :key="`badge:${row.key}:${badge.id}`"
                          class="groups-badge"
                        >
                          {{ badge.label }}
                        </span>
                      </span>
                      <span
                        v-else-if="row.displayCurrentNoSpecificGroup"
                        class="text-caption text-medium-emphasis"
                        data-testid="detailed-publish-impact-groups-vin-no-specific-group"
                      >
                        No specific group
                      </span>
                    </button>
                  </div>

                  <div
                    class="groups-details-pane"
                    data-testid="detailed-publish-impact-groups-selected-details"
                  >
                    <template v-if="selectedGroupsRow">
                      <div class="field-mapping-details-header">
                        <div>
                          <div class="text-caption text-medium-emphasis">Group membership for</div>
                          <code
                            class="vin-cell"
                            data-testid="detailed-publish-impact-groups-selected-vin"
                          >
                            {{ selectedGroupsRow.vin }}
                          </code>
                        </div>
                        <div
                          class="text-body-2 text-medium-emphasis"
                          data-testid="detailed-publish-impact-groups-selected-changed"
                        >
                          {{ selectedGroupsRow.changed ? 'Changed: Yes' : 'Changed: No' }}
                        </div>
                      </div>

                      <div class="groups-detail-section">
                        <div class="text-caption font-weight-bold mb-1">Current groups</div>
                        <div
                          v-if="selectedGroupsRow.displayCurrentGroups.length > 0"
                          class="d-flex flex-wrap ga-2"
                          data-testid="detailed-publish-impact-groups-current"
                        >
                          <span
                            v-for="badge in selectedGroupsRow.displayCurrentGroups"
                            :key="`cur:${badge.id}`"
                            class="groups-badge groups-badge--current"
                          >
                            {{ badge.label }}
                          </span>
                        </div>
                        <div
                          v-else-if="selectedGroupsRow.displayCurrentNoSpecificGroup"
                          class="text-body-2 text-medium-emphasis"
                          data-testid="detailed-publish-impact-groups-current-no-specific"
                        >
                          No specific group
                        </div>
                        <div
                          v-else
                          class="text-body-2 text-medium-emphasis"
                          data-testid="detailed-publish-impact-groups-current-empty"
                        >
                          Not assigned to any group.
                        </div>
                      </div>

                      <div
                        v-if="selectedGroupsRow.priorReadFailed"
                        class="groups-detail-section text-caption text-medium-emphasis"
                        data-testid="detailed-publish-impact-groups-prior-unavailable"
                      >
                        Previous group membership was not available for this run, so only
                        additions are shown.
                      </div>
                      <div v-else class="groups-detail-section">
                        <div class="text-caption font-weight-bold mb-1">Previous groups</div>
                        <div
                          v-if="selectedGroupsRow.displayPreviousGroups.length > 0"
                          class="d-flex flex-wrap ga-2"
                          data-testid="detailed-publish-impact-groups-previous"
                        >
                          <span
                            v-for="badge in selectedGroupsRow.displayPreviousGroups"
                            :key="`prev:${badge.id}`"
                            class="groups-badge groups-badge--previous"
                          >
                            {{ badge.label }}
                          </span>
                        </div>
                        <div
                          v-else-if="selectedGroupsRow.displayPreviousNoSpecificGroup"
                          class="text-body-2 text-medium-emphasis"
                          data-testid="detailed-publish-impact-groups-previous-no-specific"
                        >
                          No specific group
                        </div>
                        <div v-else class="text-body-2 text-medium-emphasis">None.</div>
                      </div>

                      <div
                        v-if="selectedGroupsRow.reasons.length > 0"
                        class="groups-detail-section"
                        data-testid="detailed-publish-impact-groups-reasons"
                      >
                        <div class="text-caption font-weight-bold mb-1">Reasons</div>
                        <ul class="groups-reason-list">
                          <li
                            v-for="(reason, idx) in selectedGroupsRow.reasons"
                            :key="`reason:${idx}`"
                          >
                            <div class="groups-reason-explanation">{{ reason.explanation }}</div>
                            <div
                              v-if="reason.hasCriterion"
                              class="groups-reason-criterion text-caption text-medium-emphasis"
                              data-testid="detailed-publish-impact-groups-reason-criterion"
                            >
                              <div v-if="reason.ruleName">Rule: {{ reason.ruleName }}</div>
                              <div v-if="reason.conditionText">
                                Condition: {{ reason.conditionText }}
                              </div>
                              <div v-if="reason.actualText !== undefined">
                                Actual: {{ reason.actualText || EMPTY_VALUE_SENTINEL }}
                              </div>
                            </div>
                          </li>
                        </ul>
                        <div
                          v-if="selectedGroupsRow.showRemovedCriteriaNote"
                          class="groups-reason-note text-caption text-medium-emphasis mt-1"
                          data-testid="detailed-publish-impact-groups-removed-criteria-note"
                        >
                          Detailed criteria for removed groups were not captured for this run.
                        </div>
                      </div>

                      <div
                        v-if="selectedGroupsRow.inputs.length > 0"
                        class="groups-detail-section"
                        data-testid="detailed-publish-impact-groups-inputs"
                      >
                        <div class="text-caption font-weight-bold mb-1">
                          Inputs used for grouping
                        </div>
                        <v-table density="compact" class="border rounded">
                          <thead>
                            <tr>
                              <th>Field</th>
                              <th>Value</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr
                              v-for="(input, idx) in selectedGroupsRow.inputs"
                              :key="`input:${idx}:${input.field}`"
                            >
                              <td>{{ input.field }}</td>
                              <td>{{ input.value || EMPTY_VALUE_SENTINEL }}</td>
                            </tr>
                          </tbody>
                        </v-table>
                      </div>
                    </template>

                    <v-alert v-else type="info" variant="tonal" density="comfortable">
                      No groups match this search.
                    </v-alert>
                  </div>
                </div>
              </template>

              <!--
                Website Visibility (listingRules) — default browse shows only
                vehicles that matched blocking rules; search spans all evaluated
                records. Master/detail mirrors Vehicle Groups: VIN + decision
                badge on the left, the selected VIN's rule decision on the right.
              -->
              <template v-if="isListingRulesDecisionBrowse">
                <div
                  v-if="decisionSearchStatus === 'loading'"
                  class="d-flex align-center ga-2 mb-3"
                  data-testid="detailed-publish-impact-listing-rules-search-loading"
                >
                  <v-progress-circular indeterminate color="primary" size="18" />
                  <span class="text-body-2">Searching decisions...</span>
                </div>

                <v-alert
                  v-else-if="decisionSearchStatus === 'unavailable'"
                  type="info"
                  variant="tonal"
                  density="comfortable"
                  class="mb-3"
                  data-testid="detailed-publish-impact-listing-rules-search-unavailable"
                >
                  We couldn't search decisions right now. Please try again.
                </v-alert>

                <v-alert
                  v-if="
                    decisionSearchActive &&
                    listingRulesBrowseRows.length === 0 &&
                    decisionSearchStatus !== 'loading' &&
                    decisionSearchStatus !== 'unavailable'
                  "
                  type="info"
                  variant="tonal"
                  density="comfortable"
                  class="mb-3"
                  data-testid="detailed-publish-impact-listing-rules-search-empty"
                >
                  No publish rule decisions match this search.
                </v-alert>

                <div
                  v-if="listingRulesSummaryLines.length > 0"
                  class="visibility-summary mb-3"
                  data-testid="detailed-publish-impact-visibility-summary"
                >
                  <div
                    v-for="line in listingRulesSummaryLines"
                    :key="`vis:${line}`"
                    class="text-body-2"
                  >
                    {{ line }}
                  </div>
                </div>

                <div
                  class="text-caption text-medium-emphasis mb-4"
                  data-testid="detailed-publish-impact-visibility-run-summary-helper"
                >
                  {{ RUN_SUMMARY_VISIBILITY_HELPER }}
                </div>

                <div
                  v-if="
                    !decisionSearchActive &&
                    !showListingRulesNoBlockedNotice &&
                    !showListingRulesBlockedDetailsMissingNoticeEffective &&
                    !showBlockedDecisionsPanel
                  "
                  class="text-caption text-medium-emphasis mb-4"
                  data-testid="detailed-publish-impact-listing-rules-default-helper"
                >
                  Showing vehicles that matched blocking rules. Search can find any
                  evaluated VIN.
                </div>

                <v-alert
                  v-if="showListingRulesNoBlockedNotice"
                  type="info"
                  variant="tonal"
                  density="comfortable"
                  class="mb-3"
                  data-testid="detailed-publish-impact-listing-rules-no-blocked"
                >
                  <div>No vehicles matched blocking rules in this run.</div>
                  <div>Search for a VIN to inspect its publish rule evaluation.</div>
                </v-alert>

                <v-alert
                  v-if="showListingRulesBlockedDetailsMissingNoticeEffective"
                  type="warning"
                  variant="tonal"
                  density="comfortable"
                  class="mb-3"
                  data-testid="detailed-publish-impact-listing-rules-blocked-details-missing"
                >
                  <div>{{ listingRulesBlockedDetailsMissingMessage }}</div>
                  <div>Search for a VIN to inspect its publish rule evaluation.</div>
                </v-alert>

                <!--
                  Phase 3 — Blocked vehicles drill-down. Driven by the dedicated
                  blocked-decisions artifact: one row per blocked VIN with true
                  rule attribution (authoritative run record / recovered via SRP
                  re-evaluation / unavailable). Includes already-blocked VINs the
                  per-VIN decision page misses. Shown whenever blocked rows exist.
                -->
                <div
                  v-if="showBlockedDecisionsPanel"
                  class="blocked-decisions-panel mb-4"
                  data-testid="detailed-publish-impact-blocked-decisions"
                >
                  <div
                    class="text-subtitle-2 mb-2"
                    data-testid="detailed-publish-impact-blocked-decisions-heading"
                  >
                    Blocked vehicles
                    <span class="text-medium-emphasis">({{ blockedDecisionsTotal }})</span>
                  </div>

                  <div
                    v-if="blockedDecisionsLoading && !hasBlockedDecisionRows"
                    class="d-flex align-center ga-2 mb-2"
                    data-testid="detailed-publish-impact-blocked-decisions-loading"
                  >
                    <v-progress-circular indeterminate color="primary" size="18" />
                    <span class="text-body-2">Loading blocked vehicles...</span>
                  </div>

                  <div
                    v-for="row in blockedDecisionRows"
                    :key="`blocked:${row.vin}`"
                    class="blocked-decision-row"
                    :data-testid="`detailed-publish-impact-blocked-decision-row-${row.vin}`"
                  >
                    <div class="blocked-decision-row__head">
                      <code class="vin-cell" data-testid="detailed-publish-impact-blocked-decision-vin">
                        {{ row.vin }}
                      </code>
                      <span
                        class="blocked-decision-chip"
                        :class="`blocked-decision-chip--${row.attribution}`"
                        data-testid="detailed-publish-impact-blocked-decision-attribution"
                      >
                        {{ blockedDecisionAttributionLabelFor(row) }}
                      </span>
                      <span
                        class="blocked-decision-chip blocked-decision-chip--classification"
                        data-testid="detailed-publish-impact-blocked-decision-classification"
                      >
                        {{ blockedDecisionClassificationLabelFor(row) }}
                      </span>
                    </div>
                    <div
                      class="text-body-2 text-medium-emphasis"
                      data-testid="detailed-publish-impact-blocked-decision-reason"
                    >
                      {{ blockedDecisionReasonTextFor(row) }}
                    </div>
                    <div
                      v-if="row.feedSource"
                      class="text-caption text-medium-emphasis"
                      data-testid="detailed-publish-impact-blocked-decision-feed"
                    >
                      Feed: {{ row.feedSource }}
                    </div>
                  </div>

                  <div v-if="blockedDecisionsHasMore" class="mt-2">
                    <v-btn
                      size="small"
                      variant="tonal"
                      :loading="blockedDecisionsLoading"
                      data-testid="detailed-publish-impact-blocked-decisions-load-more"
                      @click="loadMoreBlockedDecisions"
                    >
                      Load more
                    </v-btn>
                  </div>
                </div>

                <div
                  v-if="listingRulesBrowseRows.length > 0"
                  class="groups-decision-layout"
                  data-testid="detailed-publish-impact-listing-rules-decisions"
                >
                  <div
                    class="groups-vin-list"
                    data-testid="detailed-publish-impact-listing-rules-vin-list"
                  >
                    <button
                      v-for="row in listingRulesBrowseRows"
                      :key="row.key"
                      type="button"
                      class="field-mapping-vin-list-row"
                      :class="{
                        'field-mapping-vin-list-row--selected':
                          row.vin === selectedListingRulesVin,
                      }"
                      :data-testid="`detailed-publish-impact-browser-row-${row.vin}`"
                      @click="selectedListingRulesVin = row.vin"
                    >
                      <code class="vin-cell" data-testid="detailed-publish-impact-listing-rules-vin">
                        {{ row.vin }}
                      </code>
                      <span
                        class="visibility-badge"
                        :class="
                          row.blocked
                            ? 'visibility-badge--blocked'
                            : 'visibility-badge--accepted'
                        "
                        data-testid="detailed-publish-impact-listing-rules-vin-badge"
                      >
                        {{ row.decisionLabel }}
                      </span>
                    </button>
                  </div>

                  <div
                    class="groups-details-pane"
                    data-testid="detailed-publish-impact-listing-rules-selected-details"
                  >
                    <template v-if="selectedListingRulesRow">
                      <div class="field-mapping-details-header">
                        <div>
                          <div class="text-caption text-medium-emphasis">
                            Publish rule evaluation for
                          </div>
                          <code
                            class="vin-cell"
                            data-testid="detailed-publish-impact-listing-rules-selected-vin"
                          >
                            {{ selectedListingRulesRow.vin }}
                          </code>
                        </div>
                        <div
                          class="text-body-2 text-medium-emphasis"
                          data-testid="detailed-publish-impact-listing-rules-selected-decision"
                        >
                          Decision: {{ selectedListingRulesRow.decisionLabel }}
                        </div>
                      </div>

                      <div
                        v-if="selectedListingRulesRow.ruleLabel"
                        class="groups-detail-section"
                        data-testid="detailed-publish-impact-listing-rules-rule"
                      >
                        <div class="text-caption font-weight-bold mb-1">Rule</div>
                        <div class="text-body-2">{{ selectedListingRulesRow.ruleLabel }}</div>
                      </div>

                      <div
                        v-if="selectedListingRulesRow.blocked"
                        class="groups-detail-section"
                        data-testid="detailed-publish-impact-listing-rules-why-matched"
                      >
                        <div class="text-caption font-weight-bold mb-1">Why it matched</div>
                        <ul
                          v-if="selectedListingRulesRow.whyMatchedLines.length > 0"
                          class="groups-reason-list"
                        >
                          <li
                            v-for="(line, lineIdx) in selectedListingRulesRow.whyMatchedLines"
                            :key="`why:${lineIdx}`"
                            class="text-body-2"
                          >
                            {{ line }}
                          </li>
                        </ul>
                        <div v-else class="text-body-2">{{ selectedListingRulesRow.reason }}</div>
                      </div>

                      <div
                        v-else-if="selectedListingRulesRow.reason"
                        class="groups-detail-section"
                        data-testid="detailed-publish-impact-listing-rules-reason"
                      >
                        <div class="text-caption font-weight-bold mb-1">Reason</div>
                        <div class="text-body-2">{{ selectedListingRulesRow.reason }}</div>
                      </div>

                      <div
                        v-if="selectedListingRulesRow.feeds.length > 0"
                        class="groups-detail-section"
                        data-testid="detailed-publish-impact-listing-rules-feeds"
                      >
                        <div class="text-caption font-weight-bold mb-1">Available feeds</div>
                        <div class="d-flex flex-wrap ga-2">
                          <span
                            v-for="feed in selectedListingRulesRow.feeds"
                            :key="`feed:${feed}`"
                            class="groups-badge"
                          >
                            {{ feed }}
                          </span>
                        </div>
                      </div>

                      <div
                        v-if="selectedListingRulesRow.requiredFeed"
                        class="groups-detail-section"
                        data-testid="detailed-publish-impact-listing-rules-required-feed"
                      >
                        <div class="text-caption font-weight-bold mb-1">Required feed</div>
                        <div class="text-body-2">{{ selectedListingRulesRow.requiredFeed }}</div>
                      </div>

                      <div
                        v-if="selectedListingRulesRow.outcomeNote"
                        class="groups-detail-section"
                        data-testid="detailed-publish-impact-listing-rules-outcome"
                      >
                        <div class="text-caption font-weight-bold mb-1">Outcome</div>
                        <div class="text-body-2">{{ selectedListingRulesRow.outcomeNote }}</div>
                      </div>

                      <div
                        v-if="showSourceAuthorityHelper"
                        class="text-caption text-medium-emphasis"
                        data-testid="detailed-publish-impact-listing-rules-source-authority-helper"
                      >
                        {{ SOURCE_AUTHORITY_HELPER }}
                      </div>
                    </template>

                    <v-alert v-else type="info" variant="tonal" density="comfortable">
                      No publish rule decisions match this search.
                    </v-alert>
                  </div>
                </div>
              </template>

              <div
                v-if="!isGroupsDecisionBrowse && !isListingRulesDecisionBrowse"
                class="detailed-publish-impact-results-scroll"
              >
                <!--
                  Server-side search status (fieldMapping). Search runs against
                  the stage-decisions endpoint (debounced) so a VIN/field that
                  is not in the loaded browse page is still locatable.
                -->
                <div
                  v-if="isSearchableDecisionBrowse && decisionSearchStatus === 'loading'"
                  class="d-flex align-center ga-2 mb-3"
                  data-testid="detailed-publish-impact-field-mapping-search-loading"
                >
                  <v-progress-circular indeterminate color="primary" size="18" />
                  <span class="text-body-2">Searching decisions...</span>
                </div>

                <v-alert
                  v-else-if="isSearchableDecisionBrowse && decisionSearchStatus === 'unavailable'"
                  type="info"
                  variant="tonal"
                  density="comfortable"
                  class="mb-3"
                  data-testid="detailed-publish-impact-field-mapping-search-unavailable"
                >
                  We couldn't search decisions right now. Please try again.
                </v-alert>

                <v-alert
                  v-if="
                    isStageScopedDialog &&
                    !isGroupsDecisionBrowse &&
                    !isFeedFieldRulesDecisionBrowse &&
                    browserRowViewModels.length > 0 &&
                    (isFieldMappingDecisionBrowse
                      ? fieldMappingBrowseGroups.length === 0 &&
                        decisionSearchStatus !== 'loading' &&
                        decisionSearchStatus !== 'unavailable'
                      : visibleBrowserRowViewModels.length === 0)
                  "
                  type="info"
                  variant="tonal"
                  density="comfortable"
                  class="mb-3"
                  data-testid="detailed-publish-impact-stage-vin-search-empty"
                >
                  {{
                    isSearchableDecisionBrowse
                      ? 'No decisions match this search.'
                      : 'No VINs match this search.'
                  }}
                </v-alert>

                <!--
                  Field Mapping "View decisions" browse — master/detail:
                  VIN list on the left, selected VIN field changes on the
                  right. The cross-stage VIN trail still uses
                  <FieldMappingDecisionTrailRow>; this browse deliberately
                  avoids the row's expander UI.
                -->
                <div
                  v-if="isFieldMappingDecisionBrowse && fieldMappingBrowseGroups.length > 0"
                  class="field-mapping-decision-master-detail"
                  data-testid="detailed-publish-impact-field-mapping-decisions"
                >
                  <div
                    class="field-mapping-vin-list"
                    data-testid="detailed-publish-impact-field-mapping-vin-list"
                  >
                    <button
                      v-for="group in fieldMappingBrowseGroups"
                      :key="group.key"
                      type="button"
                      class="field-mapping-vin-list-row"
                      :class="{
                        'field-mapping-vin-list-row--selected':
                          group.vin === selectedFieldMappingVin,
                      }"
                      :data-testid="`detailed-publish-impact-browser-row-${group.vin}`"
                      @click="selectedFieldMappingVin = group.vin"
                    >
                      <code
                        class="vin-cell"
                        data-testid="detailed-publish-impact-field-mapping-vin"
                      >
                        {{ group.vin }}
                      </code>
                    </button>
                  </div>

                  <div
                    class="field-mapping-details-panel"
                    data-testid="detailed-publish-impact-field-mapping-selected-details"
                  >
                    <template v-if="selectedFieldMappingGroup">
                      <div class="field-mapping-details-header">
                        <div>
                          <div class="text-caption text-medium-emphasis">Field changes for</div>
                          <code
                            class="vin-cell"
                            data-testid="detailed-publish-impact-field-mapping-selected-vin"
                          >
                            {{ selectedFieldMappingGroup.vin }}
                          </code>
                        </div>
                        <div
                          class="text-body-2 text-medium-emphasis"
                          data-testid="detailed-publish-impact-field-mapping-selected-field-count"
                        >
                          {{ fieldMappingFieldCountCopy(selectedFieldMappingGroup.fieldCount) }}
                        </div>
                      </div>

                      <div
                        class="field-mapping-change-list"
                        data-testid="detailed-publish-impact-field-mapping-change-list"
                      >
                        <article
                          v-for="(change, idx) in selectedFieldMappingFieldChanges"
                          :key="`${selectedFieldMappingGroup.key}:field:${idx}:${change.field}`"
                          class="field-mapping-change-card"
                          :data-testid="`detailed-publish-impact-field-mapping-field-row-${idx}`"
                        >
                          <div
                            class="text-body-2 font-weight-medium"
                            data-testid="detailed-publish-impact-field-mapping-field-label"
                          >
                            {{ change.fieldDisplay }}
                          </div>
                          <div class="field-mapping-change-lines">
                            <div
                              class="field-mapping-change-diff text-body-1 font-weight-medium"
                              data-testid="detailed-publish-impact-field-mapping-change"
                            >
                              {{ change.changeDisplay }}
                            </div>
                            <div
                              v-if="change.explanation"
                              class="text-body-2"
                              data-testid="detailed-publish-impact-field-mapping-explanation"
                            >
                              <span class="text-medium-emphasis">Why:</span>
                              {{ change.explanation }}
                            </div>
                            <div
                              v-for="sourceLine in change.sourceLines"
                              :key="sourceLine.label"
                              class="field-mapping-source-line text-caption text-medium-emphasis"
                              data-testid="detailed-publish-impact-field-mapping-source"
                            >
                              <span>{{ sourceLine.label }}:</span>
                              {{ sourceLine.display }}
                            </div>
                          </div>
                        </article>
                      </div>
                    </template>

                    <v-alert v-else type="info" variant="tonal" density="comfortable">
                      No decisions match this search.
                    </v-alert>
                  </div>
                </div>

                <!--
                  Feed Field Rules "View feed field rule details" browse —
                  master/detail mirroring Field Mapping. VIN list on the left,
                  per-field changes on the right. An exact VIN typed in the
                  search box that is not in the loaded pages is resolved via the
                  VIN trail (server-backed) and merged into the list.
                -->
                <template v-if="isFeedFieldRulesDecisionBrowse">
                  <div
                    v-if="feedFieldRulesSearchEmpty && feedFieldRulesSearchStatus === 'loading'"
                    class="d-flex align-center ga-2 mb-3"
                    data-testid="detailed-publish-impact-feed-field-rules-search-loading"
                  >
                    <v-progress-circular indeterminate color="primary" size="18" />
                    <span class="text-body-2">Searching feed field rule updates...</span>
                  </div>

                  <v-alert
                    v-else-if="feedFieldRulesSearchEmpty && feedFieldRulesSearchStatus === 'unavailable'"
                    type="info"
                    variant="tonal"
                    density="comfortable"
                    class="mb-3"
                    data-testid="detailed-publish-impact-feed-field-rules-search-unavailable"
                  >
                    We couldn't search feed field rule updates right now. Please try again.
                  </v-alert>

                  <v-alert
                    v-else-if="feedFieldRulesSearchEmpty"
                    type="info"
                    variant="tonal"
                    density="comfortable"
                    class="mb-3"
                    data-testid="detailed-publish-impact-feed-field-rules-search-empty"
                  >
                    No feed field rule updates found for this VIN.
                  </v-alert>

                  <v-alert
                    v-else-if="feedFieldRulesBrowseGroups.length === 0"
                    type="info"
                    variant="tonal"
                    density="comfortable"
                    class="mb-3"
                    data-testid="detailed-publish-impact-feed-field-rules-browse-empty"
                  >
                    No feed field rule updates were captured for this run.
                  </v-alert>

                  <div
                    v-if="feedFieldRulesBrowseGroups.length > 0"
                    class="field-mapping-decision-master-detail"
                    data-testid="detailed-publish-impact-feed-field-rules-decisions"
                  >
                    <div
                      class="field-mapping-vin-list"
                      data-testid="detailed-publish-impact-feed-field-rules-vin-list"
                    >
                      <button
                        v-for="group in feedFieldRulesBrowseGroups"
                        :key="group.key"
                        type="button"
                        class="field-mapping-vin-list-row"
                        :class="{
                          'field-mapping-vin-list-row--selected':
                            group.vin === selectedFeedFieldRulesVin,
                        }"
                        :data-testid="`detailed-publish-impact-browser-row-${group.vin}`"
                        @click="selectedFeedFieldRulesVin = group.vin"
                      >
                        <code
                          class="vin-cell"
                          data-testid="detailed-publish-impact-feed-field-rules-vin"
                        >
                          {{ group.vin }}
                        </code>
                      </button>
                    </div>

                    <div
                      class="field-mapping-details-panel"
                      data-testid="detailed-publish-impact-feed-field-rules-selected-details"
                    >
                      <template v-if="selectedFeedFieldRulesGroup">
                        <div class="field-mapping-details-header">
                          <div>
                            <div class="text-caption text-medium-emphasis">
                              Feed field rule updates for
                            </div>
                            <code
                              class="vin-cell"
                              data-testid="detailed-publish-impact-feed-field-rules-selected-vin"
                            >
                              {{ selectedFeedFieldRulesGroup.vin }}
                            </code>
                          </div>
                          <div
                            class="text-body-2 text-medium-emphasis"
                            data-testid="detailed-publish-impact-feed-field-rules-selected-field-count"
                          >
                            {{ fieldMappingFieldCountCopy(selectedFeedFieldRulesGroup.fieldCount) }}
                          </div>
                        </div>

                        <div
                          v-if="selectedFeedFieldRulesFieldChanges.length > 0"
                          class="field-mapping-change-list"
                          data-testid="detailed-publish-impact-feed-field-rules-change-list"
                        >
                          <article
                            v-for="(change, idx) in selectedFeedFieldRulesFieldChanges"
                            :key="`${selectedFeedFieldRulesGroup.key}:field:${idx}:${change.field}`"
                            class="field-mapping-change-card"
                            :data-testid="`detailed-publish-impact-feed-field-rules-field-row-${idx}`"
                          >
                            <div
                              class="text-body-2 font-weight-medium"
                              data-testid="detailed-publish-impact-feed-field-rules-field-label"
                            >
                              {{ change.fieldDisplay }}
                            </div>
                            <div class="field-mapping-change-lines">
                              <div
                                class="field-mapping-change-diff text-body-1 font-weight-medium"
                                data-testid="detailed-publish-impact-feed-field-rules-change"
                              >
                                {{ change.changeDisplay }}
                              </div>
                              <div
                                v-if="change.feedSource"
                                class="field-mapping-source-line text-caption text-medium-emphasis"
                                data-testid="detailed-publish-impact-feed-field-rules-source"
                              >
                                <span>Feed source:</span>
                                {{ change.feedSource }}
                              </div>
                              <div
                                v-if="change.conditionSummary || change.actionSummary"
                                class="feed-field-rules-matched-rule"
                                data-testid="detailed-publish-impact-feed-field-rules-matched-rule"
                              >
                                <div class="text-caption font-weight-medium">Rule matched</div>
                                <div
                                  v-if="change.conditionSummary"
                                  class="field-mapping-source-line text-caption text-medium-emphasis"
                                  data-testid="detailed-publish-impact-feed-field-rules-condition"
                                >
                                  <span>When:</span>
                                  {{ change.conditionSummary }}
                                </div>
                                <div
                                  v-if="change.actionSummary"
                                  class="field-mapping-source-line text-caption text-medium-emphasis"
                                  data-testid="detailed-publish-impact-feed-field-rules-action"
                                >
                                  <span>Action:</span>
                                  {{ change.actionSummary }}
                                </div>
                              </div>
                              <div
                                v-else-if="change.ruleLabel"
                                class="field-mapping-source-line text-caption text-medium-emphasis"
                                data-testid="detailed-publish-impact-feed-field-rules-rule"
                              >
                                <span>Rule:</span>
                                {{ change.ruleLabel }}
                              </div>
                              <div
                                v-if="change.why"
                                class="text-body-2"
                                data-testid="detailed-publish-impact-feed-field-rules-why"
                              >
                                <span class="text-medium-emphasis">Why:</span>
                                {{ change.why }}
                              </div>
                            </div>
                          </article>
                        </div>

                        <v-alert
                          v-else
                          type="info"
                          variant="tonal"
                          density="comfortable"
                          data-testid="detailed-publish-impact-feed-field-rules-no-detail"
                        >
                          Feed field rule update details were not captured for this VIN.
                        </v-alert>
                      </template>
                    </div>
                  </div>
                </template>

                <v-table
                  v-if="
                    !isSearchableDecisionBrowse &&
                    !isFeedFieldRulesDecisionBrowse &&
                    (!isStageScopedDialog || visibleBrowserRowViewModels.length > 0)
                  "
                  density="compact"
                  class="border rounded"
                >
                  <thead>
                    <tr>
                      <th>VIN</th>
                      <th v-if="showPreProcessorReasonColumn">Reason</th>
                      <template v-else-if="showBrowserOutcomeColumns">
                        <th>Outcome</th>
                        <th>Primary reason</th>
                      </template>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="row in visibleBrowserRowViewModels"
                      :key="row.key"
                      :class="{ 'vin-row-disabled': !isStageScopedDialog && vinLookupInFlight }"
                      :data-testid="`detailed-publish-impact-browser-row-${row.vin}`"
                      @click="!isStageScopedDialog && onBrowserRowClick(row.vin)"
                    >
                      <td>
                        <code class="vin-cell">{{ row.vin }}</code>
                      </td>
                      <td
                        v-if="showPreProcessorReasonColumn"
                        class="reason-cell reason-cell--preprocessor"
                      >
                        <div class="reason-lines">
                          <div
                            v-if="row.reasonDisplay.summary"
                            class="reason-summary"
                          >
                            {{ row.reasonDisplay.summary }}
                          </div>
                          <div
                            v-for="(line, lineIdx) in row.reasonDisplay.lines"
                            :key="`${row.key}:reason:${lineIdx}`"
                            class="reason-line"
                          >
                            {{ line }}
                          </div>
                        </div>
                      </td>
                      <template v-else-if="showBrowserOutcomeColumns">
                        <td>{{ row.statusLabel }}</td>
                        <td class="text-truncate reason-cell">
                          {{ row.reasonDisplay.lines[0] || row.reasonDisplay.summary || '—' }}
                        </td>
                      </template>
                    </tr>
                  </tbody>
                </v-table>
              </div>

              <div class="d-flex align-center justify-center mt-3 ga-3 flex-wrap">
                <v-btn
                  v-if="canLoadMore"
                  size="small"
                  variant="tonal"
                  color="primary"
                  :loading="loadMoreLoading"
                  :disabled="loadMoreLoading"
                  data-testid="detailed-publish-impact-browser-load-more"
                  @click="onLoadMore"
                >
                  Load more
                </v-btn>
              </div>

              <!--
                Adjustment #3 — explicit end-of-results indicator. Shown
                ONLY when the cursor is exhausted AND we already have
                rows; an empty result has its own dedicated panel above.
              -->
              <div
                v-if="!canLoadMore"
                class="text-caption text-medium-emphasis text-center mt-2"
                data-testid="detailed-publish-impact-browser-end"
              >
                {{ isStageScopedDialog ? 'All shown' : 'End of results' }}
              </div>

              <!--
                Adjustment #1 — same micro-note as the empty state, but
                positioned below the data so it reads as a footnote on
                the table rather than an explanation of an empty page.
              -->
              <div
                v-if="store.activeBrowserHasIncompleteData"
                class="text-caption text-medium-emphasis text-center mt-1"
                data-testid="detailed-publish-impact-browser-incomplete"
              >
                Some results may not be shown.
              </div>

              <!--
                Recoverable error: rows are still rendered, but the most
                recent fetch failed (e.g. clicking "Load more" hit a 5xx).
                Surface a tonal alert so the dealer knows the table may
                not be complete and can retry.
              -->
              <v-alert
                v-if="activeBrowserStatus === 'unavailable'"
                type="info"
                variant="tonal"
                density="comfortable"
                class="mt-3"
                data-testid="detailed-publish-impact-browser-load-failed"
              >
                We couldn't load more results right now.
              </v-alert>
            </div>
          </template>
        </section>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import {
  useDetailedPublishImpactStore,
  VIN_VALIDATION,
  type PublishFlowSupportedDecisionStage,
} from '@/stores/useDetailedPublishImpactStore'
import {
  formatPreProcessorReasonDisplay,
  type FeedPreparationVinTab,
  type PreProcessorReasonDisplay,
} from '@/utils/publishFlowPreProcessorReason'
import {
  EMPTY_VALUE_SENTINEL,
  friendlyStageLabel,
  publishFlowAvailabilityCopy,
  publishFlowDecisionStatusCopy,
  publishFlowFeedFieldRuleActionLabel,
  publishFlowFeedFieldRuleFieldLabel,
  publishFlowFeedFieldRuleValueCopy,
  publishFlowFeedFieldRuleWhyCopy,
  publishFlowListingRulesVinDetailCopy,
} from '@/utils/publishFlowStageLabels'
import { formatFeedSource } from '@/utils/feedSourceFormat'
import {
  tryGetPublishFlowStageDecisions,
  tryGetPublishFlowVinTrail,
  type PublishStageDecisionsPage,
  type PublishVisibilitySummary,
} from '@/services/publishFlowDecisionService'
import {
  buildPublishEligibilityWhyMatchedLines,
  formatPublishEligibilityDialogRuleLabel,
  formatPublishEligibilityReasonExplanation,
  formatVisibilityRuleLabel,
  isGeneratedSourceAuthorityRuleLabel,
  publishEligibilityFallbackReason,
} from '@/services/publishFlowMetricsService'
import {
  blockedDecisionAttributionLabel,
  blockedDecisionClassificationLabel,
  blockedDecisionReasonText,
  type BlockedDecisionRecord,
} from '@/services/publishFlowBlockedDecisionsService'
import GroupsDecisionTrailRow from '@/components/publishFlow/GroupsDecisionTrailRow.vue'
import FieldMappingDecisionTrailRow from '@/components/publishFlow/FieldMappingDecisionTrailRow.vue'
import PreProcessorDecisionTrailRow from '@/components/publishFlow/PreProcessorDecisionTrailRow.vue'
import FeedFieldRulesDecisionTrailRow from '@/components/publishFlow/FeedFieldRulesDecisionTrailRow.vue'
import FeedPreparationPassThroughPanel from '@/components/publishFlow/FeedPreparationPassThroughPanel.vue'
import type {
  PublishDecisionRecord,
  PublishDecisionReason,
  PublishObservedStage,
  PublishStageAvailability,
} from '@/services/publishPreviewArtifactsService'

/**
 * Phase 6.y modal that consumes the actual-publish decision artifacts.
 *
 * Mounted once by `PublishFlow.vue` and shown/hidden via the store's
 * `dialogOpen` flag so its internal state survives close-and-reopen.
 *
 * The dialog NEVER replaces existing publish-flow cards — they
 * continue to render below. Closing the dialog leaves them intact.
 *
 * Auth/IDs are passed in as props rather than read from `accountSettings`
 * directly so this component is trivially testable and so an unmounted
 * (no-auth) state is impossible.
 *
 * Phase 7.y additions
 * -------------------
 * - The browser is tab-driven when the manifest carries more than one
 *   decision stage. The `loadFirstPage(stage)` call is idempotent
 *   thanks to the store-side gate (Guardrail #2), so the dialog can
 *   safely re-fire it on every dialog-open + tab-change without
 *   stacking duplicate fetches.
 * - The VIN-trail row builder branches on `stage === 'groups'` and
 *   delegates to `<GroupsDecisionTrailRow>`. Publish rules rows are
 *   unchanged.
 *
 * Phase 8.y additions
 * -------------------
 * - Tab strip and default-tab cascade extend to `'fieldMapping'` in
 *   UX-priority order: listingRules -> fieldMapping -> groups (G1).
 * - The VIN-trail row builder gains a `'fieldMapping'` branch that
 *   delegates to `<FieldMappingDecisionTrailRow>` (compact summary
 *   + lazy per-field expander). Groups and listingRules behavior
 *   unchanged. Per G7, every observed stage keeps emitting an
 *   explicit per-area status line even when fieldMapping is the
 *   only stage carrying records.
 *
 * Phase 10.4.y additions
 * ----------------------
 * - Tab narrowing extends to `'preProcessor'` and preserves the
 *   store-owned approved order:
 *   preProcessor → feedFieldRules →
 *   fieldMapping → groups → listingRules.
 * - The VIN-trail row builder gains a `'preProcessor'` branch that
 *   delegates to `<PreProcessorDecisionTrailRow>` for real records.
 *   FlowMetrics-only/no-pointer entries stay on the shared
 *   status-only "Not captured for this run" path.
 *
 * Phase 10.3.y additions
 * ----------------------
 * - Tab narrowing extended to accept `'feedFieldRules'`. Stage
 *   order is owned by the store's `BROWSER_STAGES` list so the tab
 *   strip stays in sync without duplicating the literal here.
 * - The VIN-trail row builder gains a `'feedFieldRules'` branch
 *   that delegates to `<FeedFieldRulesDecisionTrailRow>` for
 *   records and falls back to the shared status-only "Was not
 *   affected" copy when no record matched the VIN — parity with the
 *   Phase 8.y / 9.y branches.
 */

export type DetailedPublishStageAction = {
  id: string
  label: string
  icon?: string
  loading?: boolean
  disabled?: boolean
}

const props = withDefaults(
  defineProps<{
    endpoint: string
    jazelId: string
    correlationId: string
    sessionToken: string
    stageDetailLines?: string[]
    stageDetailActions?: DetailedPublishStageAction[]
    /**
     * Authoritative publish-rule totals from
     * `flowMetrics.publishWide.visibilityListingRules`. Preferred over the
     * per-VIN decision page's `visibility` for the listingRules summary + notices
     * so the modal body never disagrees with the page/header (e.g. when per-VIN
     * decision rows under-report blocked VINs due to the deferred Phase 3 artifact
     * issue).
     */
    authoritativeVisibility?: {
      evaluated: number | null
      accepted: number | null
      blocked: number | null
    } | null
  }>(),
  {
    stageDetailLines: () => [],
    stageDetailActions: () => [],
    authoritativeVisibility: null,
  },
)

defineEmits<{
  (event: 'stage-detail-action', actionId: string): void
}>()

const store = useDetailedPublishImpactStore()

const isStageScopedDialog = computed(() => Boolean(store.browserStageScope))
const isPassThroughDialog = computed(() => store.dialogMode === 'passThrough')
/** Self-contained panel modes suppress the stage browser + VIN lookup. */
const isPanelModeDialog = computed(() => isPassThroughDialog.value)

/**
 * Display label for a decision stage within this (Publish Flow) dialog. The
 * `listingRules` stage is presented as "Publish Eligibility" because publish
 * rules gate inventory eligibility (archive / keep out of live inventory)
 * rather than merely toggling website visibility. Kept local so other surfaces
 * (Publish Preview, run breakdown) retain their own stage labels.
 */
function stageDisplayLabel(stage: PublishObservedStage | string): string {
  if (stage === 'listingRules') return 'Publish Eligibility'
  return friendlyStageLabel(stage)
}

const dialogTitle = computed(() => {
  if (isPassThroughDialog.value) return friendlyStageLabel('preProcessor')
  const stage = store.browserStageScope
  if (stage === 'fieldMapping') return 'Field Mapping Decisions'
  return stage ? stageDisplayLabel(stage) : 'Detailed publish results'
})

const browserHeading = computed(() => {
  const stage = store.browserStageScope

  if (stage === 'preProcessor') {
    if (preProcessorDecisionFilter.value === 'continued') return 'Continued VINs'
    return 'Filtered-out VINs'
  }
  if (stage === 'fieldMapping') return 'Field Mapping Decisions'
  if (stage === 'groups') return 'Vehicle Groups'
  if (stage === 'listingRules') return 'Publish eligibility VINs'

  return stage ? `${friendlyStageLabel(stage)} VINs` : 'Browse vehicle results for this run'
})

const stageDetailLines = computed(() => props.stageDetailLines ?? [])
const stageDetailActions = computed(() => props.stageDetailActions ?? [])

const stageDetailSummary = computed(() => {
  const lines = stageDetailLines.value.map((line) => String(line || '').trim()).filter(Boolean)

  return lines.length > 0 ? lines.join(' · ') : ''
})

const totalVisibleVinCount = computed(() =>
  isFieldMappingDecisionBrowse.value
    ? fieldMappingAllGroups.value.length
    : isFeedFieldRulesDecisionBrowse.value
      ? feedFieldRulesAllGroups.value.length
      : isGroupsDecisionBrowse.value
        ? groupsHeadingTotal.value
        : browserRowViewModels.value.length,
)
const filteredVisibleVinCount = computed(() =>
  isFieldMappingDecisionBrowse.value
    ? fieldMappingBrowseGroups.value.length
    : isFeedFieldRulesDecisionBrowse.value
      ? feedFieldRulesBrowseGroups.value.length
      : isGroupsDecisionBrowse.value
        ? groupsHeadingShown.value
        : visibleBrowserRowViewModels.value.length,
)

const browserHeadingWithCount = computed(() => {
  const heading = browserHeading.value

  if (store.browserStageScope === 'preProcessor') {
    const count =
      preProcessorDecisionFilter.value === 'continued'
        ? preProcessorContinuedCount.value
        : preProcessorFilteredOutCount.value
    return `${heading} (${count})`
  }

  if (!isStageScopedDialog.value) return heading

  const total = totalVisibleVinCount.value
  const shown = filteredVisibleVinCount.value
  const q = stageVinSearch.value.trim()

  if (total <= 0) return heading
  if (q && shown !== total) return `${heading} (${shown} of ${total})`

  return `${heading} (${total})`
})

const vinInput = ref('')
const stageVinSearch = ref('')
const preProcessorDecisionFilter = ref<FeedPreparationVinTab>('continued')
const vinMaxLength = VIN_VALIDATION.maxLength

const showBrowserOutcomeColumns = computed(() => !isStageScopedDialog.value)

const stageSearchLabel = computed(() => {
  if (store.activeBrowserStage === 'fieldMapping') return 'Search VIN or field'
  if (store.activeBrowserStage === 'groups') return 'Search VIN or group'
  if (store.activeBrowserStage === 'listingRules') return 'Search VIN or rule'
  return 'Search VIN'
})

const showPreProcessorReasonColumn = computed(
  () => store.browserStageScope === 'preProcessor',
)

const preProcessorStageSummary = computed(() => store.manifest?.stages?.preProcessor ?? null)

function finiteNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

const preProcessorSummaryInputCount = computed(() => {
  const stage = preProcessorStageSummary.value
  return finiteNumber(stage?.details?.input?.count) ?? finiteNumber(stage?.counts?.evaluated)
})

const preProcessorSummaryOutputCount = computed(() => {
  const stage = preProcessorStageSummary.value
  return (
    finiteNumber(stage?.details?.output?.count) ??
    finiteNumber(stage?.counts?.unchanged) ??
    finiteNumber(stage?.counts?.changed)
  )
})

const preProcessorSummarySkippedCount = computed(() => {
  const stage = preProcessorStageSummary.value
  const explicitSkipped = finiteNumber(stage?.counts?.skipped)
  if (explicitSkipped !== null) return explicitSkipped

  const input = preProcessorSummaryInputCount.value
  const output = preProcessorSummaryOutputCount.value
  if (input !== null && output !== null) return Math.max(0, input - output)

  return null
})

const preProcessorContinuedRecords = computed(() =>
  store.activeBrowserRows.filter((record) => record.status === 'changed'),
)

const preProcessorFilteredOutRecords = computed(() =>
  store.activeBrowserRows.filter((record) => record.status === 'skipped'),
)

const loadedPreProcessorDecisionRecordCount = computed(() => store.activeBrowserRows.length)

const preProcessorContinuedCount = computed(
  () => preProcessorSummaryOutputCount.value ?? preProcessorContinuedRecords.value.length,
)

const preProcessorFilteredOutCount = computed(
  () => preProcessorSummarySkippedCount.value ?? preProcessorFilteredOutRecords.value.length,
)

const preProcessorDecisionFilterOptions = computed(() => [
  {
    value: 'continued' as const,
    label: `Continued VINs (${preProcessorContinuedCount.value})`,
  },
  {
    value: 'filteredOut' as const,
    label: `Filtered-out VINs (${preProcessorFilteredOutCount.value})`,
  },
])

function resolveDefaultPreProcessorTab(): FeedPreparationVinTab {
  return preProcessorContinuedCount.value > 0 ? 'continued' : 'filteredOut'
}

const showPreProcessorDecisionFilter = computed(
  () =>
    store.browserStageScope === 'preProcessor' && loadedPreProcessorDecisionRecordCount.value > 0,
)

// =============================================================================
// Header subtitle — Adjustment #5
// "Based on this completed publish run (updated <relative-time>)."
// Falls back to the no-timestamp variant rather than rendering a partial
// sentence when `manifest.generatedAt` is missing.
// =============================================================================

const headerSubtitle = computed(() => {
  const generatedAt = store.manifest?.generatedAt
  if (!generatedAt) {
    return 'Based on this completed publish run.'
  }
  return `Based on this completed publish run (updated ${formatRelativeTime(generatedAt)}).`
})

const dialogSubtitle = computed(() => {
  if (isFieldMappingDecisionBrowse.value) {
    const count = fieldMappingAllGroups.value.length
    return count > 0
      ? `${count} ${count === 1 ? 'vehicle had' : 'vehicles had'} mapping updates.`
      : 'Select a VIN to see what changed.'
  }

  return !isStageScopedDialog.value && !isPanelModeDialog.value ? headerSubtitle.value : ''
})

// =============================================================================
// Phase 7.y — tab visibility / scope banner
// =============================================================================

const showStageTabs = computed(
  () => !store.browserStageScope && store.availableBrowserStages.length > 1,
)

/**
 * Phase 6.y baseline: the "publish rules only" scope banner is
 * preserved verbatim when the manifest carries listingRules and
 * NOTHING ELSE. The moment groups (or any future stage) joins the
 * manifest, the banner is suppressed because the message no longer
 * matches reality.
 */
const renderListingRulesOnlyBanner = computed(() => {
  const stages = store.availableBrowserStages
  return stages.length === 1 && stages[0] === 'listingRules'
})

const activeStage = computed(() => store.activeBrowserStage)
const activeBrowserStatus = computed(() => store.activeBrowserSlice?.status ?? 'idle')
const activeBrowserPagesLength = computed(() => store.activeBrowserSlice?.pages.length ?? 0)

const filteredOutDetailsNote = computed(() => {
  if (store.browserStageScope !== 'preProcessor') return ''
  if (loadedPreProcessorDecisionRecordCount.value > 0) return ''
  return 'Filtered-out VIN details are not available for this run.'
})

function stageTabLabel(stage: PublishFlowSupportedDecisionStage): string {
  return stageDisplayLabel(stage)
}

/**
 * Stages accepted from `v-tabs` @update:model-value. Must match
 * `PublishFlowSupportedDecisionStage` so `setActiveBrowserStage` only
 * receives decision-artifact tab keys (same set the manifest/browser use).
 */
const SUPPORTED_BROWSER_TAB_STAGES = [
  'preProcessor',
  'feedFieldRules',
  'fieldMapping',
  'groups',
  'listingRules',
] as const satisfies readonly PublishFlowSupportedDecisionStage[]

const supportedBrowserTabStageSet = new Set<string>(SUPPORTED_BROWSER_TAB_STAGES)

function onTabChange(value: unknown) {
  if (typeof value !== 'string' || !supportedBrowserTabStageSet.has(value)) return
  const stage = value as PublishFlowSupportedDecisionStage
  if (stage === store.activeBrowserStage) return
  store.setActiveBrowserStage(stage)
}

// =============================================================================
// VIN lookup
// =============================================================================

const vinLookupInFlight = computed(() => store.vinTrailStatus === 'loading')
const vinLookupDisabled = computed(
  () => vinLookupInFlight.value || vinInput.value.trim().length === 0,
)

async function submitVinLookup() {
  if (vinLookupInFlight.value) return
  await store.lookupVin({
    endpoint: props.endpoint,
    jazelId: props.jazelId,
    correlationId: props.correlationId,
    sessionToken: props.sessionToken,
    vin: vinInput.value,
  })
}

function closeDialog() {
  store.closeDialog()
}

function onDialogVisibilityChange(next: boolean | null | undefined) {
  if (next === false) closeDialog()
}

onBeforeUnmount(() => {
  if (store.dialogOpen) {
    store.closeDialog()
  }
})

// =============================================================================
// VIN trail view-model
// =============================================================================

interface VinRowBase {
  /** Composite key — guarantees uniqueness even if multiple records target one stage. */
  key: string
  stage: PublishObservedStage | string
  label: string
}

/**
 * Listing-rules-style row: status-only label, no structured diff.
 * Used for every stage other than `'groups'`.
 */
interface VinRowStatus extends VinRowBase {
  kind: 'status'
  statusLabel: string
  detail?: string
}

/**
 * Phase 7.y — groups row carries the raw decision records so the
 * `<GroupsDecisionTrailRow>` sub-component can render the structured
 * Added / Removed / Reasons view per record.
 */
interface VinRowGroups extends VinRowBase {
  kind: 'groups'
  records: PublishDecisionRecord[]
}

/**
 * Phase 8.y — fieldMapping row carries the raw decision records so
 * the `<FieldMappingDecisionTrailRow>` sub-component can render
 * the compact "N fields updated" summary plus the lazy expander
 * per record (option C).
 */
interface VinRowFieldMapping extends VinRowBase {
  kind: 'fieldMapping'
  records: PublishDecisionRecord[]
}

interface VinRowPreProcessor extends VinRowBase {
  kind: 'preProcessor'
  records: PublishDecisionRecord[]
}

/**
 * Phase 10.3.y — feedFieldRules row carries the raw decision
 * records so the `<FeedFieldRulesDecisionTrailRow>` sub-component
 * can render the dealer-friendly feed-field summary. Records
 * with no match (the writer's "no rules / fallback" path) fall
 * through to the shared status-only "Was not affected" copy via the
 * empty-records branch in `vinTrailRows`.
 */
interface VinRowFeedFieldRules extends VinRowBase {
  kind: 'feedFieldRules'
  records: PublishDecisionRecord[]
}

type VinRow =
  | VinRowStatus
  | VinRowGroups
  | VinRowFieldMapping
  | VinRowPreProcessor
  | VinRowFeedFieldRules

/**
 * Build the per-stage rendered rows. Iterates EVERY observed stage in
 * the trail, even unsupported/deferred ones, so the dealer sees the
 * full picture: "Publish rules — Was updated. Vehicle groups — Not
 * captured for this run." etc. The scope banner above the form (and
 * the friendly "Not captured" copy from `publishFlowAvailabilityCopy`)
 * keeps the messaging clear.
 *
 * Past-tense copy comes from `publishFlowStageLabels.ts`; we never
 * import the Preview helpers here so future changes to past/present
 * tense stay isolated.
 */
const vinTrailRows = computed<VinRow[]>(() => {
  const trail = store.vinTrail
  if (!trail || !trail.stages) return []
  const rows: VinRow[] = []
  for (const [stage, entry] of Object.entries(trail.stages)) {
    if (!entry) continue
    const stageId = stage as PublishObservedStage
    const label = stageDisplayLabel(stageId)
    const availability: PublishStageAvailability = entry.availability
    if (availability === 'available') {
      const recordList = Array.isArray(entry.records) ? entry.records : []
      // Phase 7.y — groups gets the structured diff renderer.
      // Phase 8.y — fieldMapping gets the lazy summary+expander
      // renderer (option C).
      // Publish rules (and every other stage) keeps the Phase 6.y
      // status-only label. Per G7, even when only one structured
      // stage has records, every other instrumented stage still
      // emits explicit per-area status lines below — the dealer
      // sees a complete per-area picture, never an implicit
      // "everything else missing" inference.
      if (stageId === 'groups') {
        if (recordList.length === 0) {
          // No record for this VIN under groups → fall through to the
          // status-only "Was not affected" line, same as listingRules
          // would produce. The dealer sees a consistent shape.
          const empty = publishFlowAvailabilityCopy(availability, { hasRecord: false })
          rows.push({
            kind: 'status',
            key: `${stage}:empty`,
            stage: stageId,
            label,
            statusLabel: empty.label,
          })
          continue
        }
        rows.push({
          kind: 'groups',
          key: `${stage}:groups`,
          stage: stageId,
          label,
          records: recordList,
        })
        continue
      }
      if (stageId === 'fieldMapping') {
        if (recordList.length === 0) {
          // No record for this VIN under fieldMapping → status row
          // with "Was not affected", parity with the groups branch.
          const empty = publishFlowAvailabilityCopy(availability, { hasRecord: false })
          rows.push({
            kind: 'status',
            key: `${stage}:empty`,
            stage: stageId,
            label,
            statusLabel: empty.label,
          })
          continue
        }
        rows.push({
          kind: 'fieldMapping',
          key: `${stage}:fieldMapping`,
          stage: stageId,
          label,
          records: recordList,
        })
        continue
      }
      if (stageId === 'preProcessor') {
        if (recordList.length === 0) {
          const empty = publishFlowAvailabilityCopy(availability, { hasRecord: false })
          rows.push({
            kind: 'status',
            key: `${stage}:empty`,
            stage: stageId,
            label,
            statusLabel: empty.label,
          })
          continue
        }
        rows.push({
          kind: 'preProcessor',
          key: `${stage}:preProcessor`,
          stage: stageId,
          label,
          records: recordList,
        })
        continue
      }
      if (stageId === 'feedFieldRules') {
        if (recordList.length === 0) {
          // No record for this VIN under feedFieldRules → status
          // row with "Was not affected", parity with the other
          // structured-stage branches. The dealer sees a consistent
          // shape regardless of which stage produced the empty echo.
          const empty = publishFlowAvailabilityCopy(availability, { hasRecord: false })
          rows.push({
            kind: 'status',
            key: `${stage}:empty`,
            stage: stageId,
            label,
            statusLabel: empty.label,
          })
          continue
        }
        rows.push({
          kind: 'feedFieldRules',
          key: `${stage}:feedFieldRules`,
          stage: stageId,
          label,
          records: recordList,
        })
        continue
      }
      if (stageId === 'listingRules') {
        if (recordList.length === 0) {
          const empty = publishFlowAvailabilityCopy(availability, { hasRecord: false })
          rows.push({
            kind: 'status',
            key: `${stage}:empty`,
            stage: stageId,
            label,
            statusLabel: empty.label,
          })
          continue
        }
        for (let idx = 0; idx < recordList.length; idx++) {
          const record = recordList[idx]
          const status = typeof record.status === 'string' ? record.status : ''
          rows.push({
            kind: 'status',
            key: `${stage}:${idx}`,
            stage: stageId,
            label,
            statusLabel: publishFlowDecisionStatusCopy(status),
            detail: publishFlowListingRulesVinDetailCopy(record),
          })
        }
        continue
      }
      if (recordList.length === 0) {
        // Stage was evaluated but this VIN had no record — surface the
        // "Was not affected" line rather than treating it as missing
        // data.
        const empty = publishFlowAvailabilityCopy(availability, { hasRecord: false })
        rows.push({
          kind: 'status',
          key: `${stage}:empty`,
          stage: stageId,
          label,
          statusLabel: empty.label,
        })
        continue
      }
      // One row per record. The backend may emit more than one record
      // per VIN per stage in future stages (e.g. multiple rule hits),
      // so iterate rather than collapsing to the first.
      for (let idx = 0; idx < recordList.length; idx++) {
        const record = recordList[idx]
        const status = typeof record.status === 'string' ? record.status : ''
        rows.push({
          kind: 'status',
          key: `${stage}:${idx}`,
          stage: stageId,
          label,
          statusLabel: publishFlowDecisionStatusCopy(status),
          detail: publishFlowAvailabilityCopy(availability, { hasRecord: true }).label,
        })
      }
    } else {
      const friendly = publishFlowAvailabilityCopy(availability, {
        reason: entry.reason,
      })
      rows.push({
        kind: 'status',
        key: `${stage}:availability`,
        stage: stageId,
        label,
        statusLabel: friendly.label,
        detail: friendly.detail,
      })
    }
  }
  return rows
})

// =============================================================================
// Phase 6.z — Browser view model
// =============================================================================

interface BrowserRow {
  key: string
  vin: string
  statusLabel: string
  reasonDisplay: PreProcessorReasonDisplay
}

const activeBrowserRowsForDisplay = computed<PublishDecisionRecord[]>(() => {
  const rows = store.activeBrowserRows
  if (store.browserStageScope !== 'preProcessor') return rows

  if (preProcessorDecisionFilter.value === 'continued') {
    return rows.filter((record) => record.status === 'changed')
  }
  if (preProcessorDecisionFilter.value === 'filteredOut') {
    return rows.filter((record) => record.status === 'skipped')
  }

  return rows.filter((record) => record.status === 'changed')
})

const browserRowViewModels = computed<BrowserRow[]>(() => {
  const rows: BrowserRow[] = []
  let idx = 0
  const activeStageKey = store.activeBrowserStage
  for (const record of activeBrowserRowsForDisplay.value) {
    const status = typeof record.status === 'string' ? record.status : ''
    rows.push({
      // Guard against duplicate VINs across pages (multi-rule hits)
      // by mixing in an index — the backend currently emits one record
      // per VIN for listingRules, but the table should remain robust
      // if that contract evolves.
      key: `${record.vin}:${idx}`,
      vin: String(record.vin || ''),
      statusLabel: publishFlowDecisionStatusCopy(status),
      reasonDisplay:
        activeStageKey === 'preProcessor'
          ? formatPreProcessorReasonDisplay(record, preProcessorDecisionFilter.value)
          : { lines: [primaryReasonForRecord(record)] },
    })
    idx += 1
  }
  return rows
})

function primaryReasonForRecord(record: PublishDecisionRecord): string {
  const primaryReason =
    Array.isArray(record.reasons) && record.reasons.length > 0
      ? String(record.reasons[0]?.explanation ?? '').trim()
      : ''
  return primaryReason || '—'
}

const visibleBrowserRowViewModels = computed<BrowserRow[]>(() => {
  if (!isStageScopedDialog.value) return browserRowViewModels.value

  // fieldMapping renders its own master/detail (not this v-table) and searches
  // server-side, so this client filter only applies to the other stage-scoped
  // browses (e.g. listingRules "Website visibility VINs").
  if (isFieldMappingDecisionBrowse.value) return browserRowViewModels.value

  const q = stageVinSearch.value.trim().toUpperCase()
  if (!q) return browserRowViewModels.value

  return browserRowViewModels.value.filter((row) => row.vin.toUpperCase().includes(q))
})

// =============================================================================
// Field Mapping decisions browse (stage-scoped "View decisions").
//
// The fieldMapping decision records already carry full per-field
// `reasons[]` (field / beforeValue / afterValue / sourceAfter /
// explanation). For the stage-scoped browse, render a local master-detail
// view so the VIN list stays light and only the selected VIN's fields appear
// in the detail panel. The cross-stage VIN trail still uses
// <FieldMappingDecisionTrailRow> unchanged.
// =============================================================================

interface FieldMappingFieldChangeView {
  field: string
  fieldDisplay: string
  beforeDisplay: string
  afterDisplay: string
  changeDisplay: string
  sourceLines: FieldMappingSourceLine[]
  explanation: string
}

interface FieldMappingSourceLine {
  label: 'Source' | 'Before source' | 'After source' | 'Previous source' | 'Feed source'
  display: string
}

interface FieldMappingVinGroup {
  key: string
  vin: string
  reasons: PublishDecisionReason[]
  fieldChanges: FieldMappingFieldChangeView[]
  fieldCount: number
}

const isFieldMappingDecisionBrowse = computed(
  () => isStageScopedDialog.value && store.activeBrowserStage === 'fieldMapping',
)

const isGroupsDecisionBrowse = computed(
  () => isStageScopedDialog.value && store.activeBrowserStage === 'groups',
)

const isListingRulesDecisionBrowse = computed(
  () => isStageScopedDialog.value && store.activeBrowserStage === 'listingRules',
)

const isFeedFieldRulesDecisionBrowse = computed(
  () => isStageScopedDialog.value && store.activeBrowserStage === 'feedFieldRules',
)

const selectedFieldMappingVin = ref('')

// -----------------------------------------------------------------------------
// Server-side fieldMapping search.
//
// The browse list only ever holds the loaded pages, so a VIN/field that lives
// past the first page is invisible to a pure client filter. When the search
// box is non-empty we (debounced) hit the stage-decisions endpoint with
// `?search=` — the backend filters across the WHOLE artifact (VIN OR
// reasons[].field) and paginates the matched set. When the box is cleared we
// fall back to the store's paged browse rows unchanged.
// -----------------------------------------------------------------------------

type DecisionSearchStatus = 'idle' | 'loading' | 'ok' | 'unavailable'

const DECISION_SEARCH_DEBOUNCE_MS = 250
const DECISION_SEARCH_PAGE_SIZE = 50

const decisionSearchStatus = ref<DecisionSearchStatus>('idle')
const decisionSearchRecords = ref<PublishDecisionRecord[]>([])
const decisionSearchCursor = ref<string | undefined>(undefined)
/** The committed term that produced `decisionSearchRecords`. */
const decisionSearchTerm = ref('')
/** Monotonic guard so stale responses (fast retyping) can never win. */
let decisionSearchSeq = 0
let decisionSearchDebounce: ReturnType<typeof setTimeout> | undefined

/**
 * Stages that support server-side `?search=` (filter-before-paginate over the
 * whole artifact). fieldMapping matches VIN/field; groups matches VIN/group.
 * Only one stage is active at a time, so a single search state is shared.
 */
const isSearchableDecisionBrowse = computed(
  () =>
    isFieldMappingDecisionBrowse.value ||
    isGroupsDecisionBrowse.value ||
    isListingRulesDecisionBrowse.value,
)

const decisionSearchActive = computed(
  () => isSearchableDecisionBrowse.value && stageVinSearch.value.trim().length > 0,
)

/**
 * Records feeding the master/detail. In search mode they come from the
 * server-filtered results; otherwise from the store's paged browse rows.
 */
const decisionSearchSourceRecords = computed<PublishDecisionRecord[]>(() =>
  decisionSearchActive.value
    ? decisionSearchRecords.value
    : activeBrowserRowsForDisplay.value,
)

const fieldMappingAllGroups = computed<FieldMappingVinGroup[]>(() => {
  if (!isFieldMappingDecisionBrowse.value) return []
  const byVin = new Map<string, FieldMappingVinGroup>()

  decisionSearchSourceRecords.value.forEach((record, idx) => {
    const vin = String(record.vin || '').trim()
    if (!vin) return

    const reasons = validFieldMappingReasons(record.reasons)
    const existing = byVin.get(vin)
    if (existing) {
      existing.reasons.push(...reasons)
      existing.fieldChanges.push(
        ...reasons.map((reason) => buildFieldMappingFieldChangeView(reason, record)),
      )
      existing.fieldCount = existing.fieldChanges.length
      return
    }

    byVin.set(vin, {
      key: `${vin}:${idx}`,
      vin,
      reasons: [...reasons],
      fieldChanges: reasons.map((reason) => buildFieldMappingFieldChangeView(reason, record)),
      fieldCount: reasons.length,
    })
  })

  return Array.from(byVin.values())
})

// Search is resolved server-side, so the left list is exactly the matched
// VINs — no additional client filtering. Kept as a named computed so the
// template/count call-sites stay descriptive.
const fieldMappingBrowseGroups = computed<FieldMappingVinGroup[]>(
  () => fieldMappingAllGroups.value,
)

const selectedFieldMappingGroup = computed<FieldMappingVinGroup | null>(() => {
  if (!selectedFieldMappingVin.value) return null
  return (
    fieldMappingBrowseGroups.value.find((group) => group.vin === selectedFieldMappingVin.value) ??
    null
  )
})

const selectedFieldMappingFieldChanges = computed<FieldMappingFieldChangeView[]>(() => {
  const group = selectedFieldMappingGroup.value
  if (!group) return []
  // When the search term matched on a FIELD (not the VIN), narrow the detail
  // table to just the matching fields so the result reads like a field hit.
  const q = stageVinSearch.value.trim().toUpperCase()
  if (!q || group.vin.toUpperCase().includes(q)) return group.fieldChanges
  const narrowed = group.fieldChanges.filter((change) => change.field.toUpperCase().includes(q))
  return narrowed.length > 0 ? narrowed : group.fieldChanges
})

function validFieldMappingReasons(reasons: PublishDecisionRecord['reasons']): PublishDecisionReason[] {
  if (!Array.isArray(reasons)) return []
  return reasons.filter(
    (reason): reason is PublishDecisionReason =>
      Boolean(reason) &&
      typeof reason === 'object' &&
      typeof reason.field === 'string' &&
      reason.field.length > 0,
  )
}

/** Reset all server-search state back to browse mode. */
function resetDecisionSearch(): void {
  if (decisionSearchDebounce) {
    clearTimeout(decisionSearchDebounce)
    decisionSearchDebounce = undefined
  }
  // Bump the sequence so any in-flight request is ignored on arrival.
  decisionSearchSeq += 1
  decisionSearchStatus.value = 'idle'
  decisionSearchRecords.value = []
  decisionSearchCursor.value = undefined
  decisionSearchTerm.value = ''
}

/**
 * Active searchable stage for the server `?search=` request. Only fieldMapping
 * and groups support it; anything else returns `null` so the caller no-ops.
 */
function searchableStage(): PublishFlowSupportedDecisionStage | null {
  const stage = store.activeBrowserStage
  return stage === 'fieldMapping' || stage === 'groups' || stage === 'listingRules'
    ? stage
    : null
}

async function runDecisionSearch(term: string): Promise<void> {
  const stage = searchableStage()
  if (!stage) return
  const seq = (decisionSearchSeq += 1)
  decisionSearchStatus.value = 'loading'
  const result = await tryGetPublishFlowStageDecisions({
    endpoint: props.endpoint,
    jazelId: props.jazelId,
    correlationId: props.correlationId,
    stage,
    sessionToken: props.sessionToken,
    pageSize: DECISION_SEARCH_PAGE_SIZE,
    search: term,
  })
  if (seq !== decisionSearchSeq) return

  if (result.status === 'ok') {
    decisionSearchTerm.value = term
    decisionSearchRecords.value = Array.isArray(result.data.records) ? result.data.records : []
    decisionSearchCursor.value = result.data.nextCursor
    decisionSearchStatus.value = 'ok'
    return
  }
  if (result.status === 'notFound') {
    // No artifact / nothing to search → behave like an empty match set.
    decisionSearchTerm.value = term
    decisionSearchRecords.value = []
    decisionSearchCursor.value = undefined
    decisionSearchStatus.value = 'ok'
    return
  }
  decisionSearchStatus.value = 'unavailable'
}

async function loadMoreDecisionSearch(): Promise<void> {
  if (decisionSearchStatus.value === 'loading') return
  const cursor = decisionSearchCursor.value
  if (!cursor) return
  const stage = searchableStage()
  if (!stage) return
  const term = decisionSearchTerm.value
  const seq = (decisionSearchSeq += 1)
  decisionSearchStatus.value = 'loading'
  const result = await tryGetPublishFlowStageDecisions({
    endpoint: props.endpoint,
    jazelId: props.jazelId,
    correlationId: props.correlationId,
    stage,
    sessionToken: props.sessionToken,
    pageSize: DECISION_SEARCH_PAGE_SIZE,
    cursor,
    search: term,
  })
  if (seq !== decisionSearchSeq) return

  if (result.status === 'ok') {
    const next = Array.isArray(result.data.records) ? result.data.records : []
    decisionSearchRecords.value = [...decisionSearchRecords.value, ...next]
    decisionSearchCursor.value = result.data.nextCursor
    decisionSearchStatus.value = 'ok'
    return
  }
  decisionSearchStatus.value = 'unavailable'
}

function reconcileSelectedFieldMappingVin(): void {
  if (!isFieldMappingDecisionBrowse.value) {
    selectedFieldMappingVin.value = ''
    return
  }

  const groups = fieldMappingBrowseGroups.value
  if (groups.length === 0) {
    selectedFieldMappingVin.value = ''
    return
  }

  if (groups.some((group) => group.vin === selectedFieldMappingVin.value)) return
  selectedFieldMappingVin.value = groups[0].vin
}

function fieldMappingFieldCountCopy(count: number): string {
  return `${count} ${count === 1 ? 'field' : 'fields'} updated`
}

// =============================================================================
// Feed Field Rules decisions browse (stage-scoped "View feed field rule
// details").
//
// Mirrors the Field Mapping master/detail: a searchable VIN list on the left
// and the selected VIN's per-field changes on the right. Each changed field is
// sourced from the record's `reasons[]` (field / before / after / feed source /
// action / explanation), reusing the same shared label helpers as the
// cross-stage <FeedFieldRulesDecisionTrailRow> so formatting stays consistent.
//
// Search differs from fieldMapping by design: feed field rule records expose a
// VIN-trail lookup (not a `?search=` browse filter), so an exact VIN that lives
// past the loaded pages is resolved via `tryGetPublishFlowVinTrail` and merged
// into the list. A term that already matches a loaded VIN never hits the
// network.
// =============================================================================

interface FeedFieldRulesFieldChangeView {
  /** Raw target field (kept for stable keys / future field-narrowing). */
  field: string
  fieldDisplay: string
  /** "<before> → <after>" with friendly value formatting. */
  changeDisplay: string
  /** Humanized feed source; empty string when none was captured. */
  feedSource: string
  /** Optional action label; null when no usable action exists (never invented). */
  ruleLabel: string | null
  /** Matched-rule condition line, e.g. "Discount is greater than 0"; null when absent. */
  conditionSummary: string | null
  /** Matched-rule action line, e.g. 'Set MSRP to "2500"'; null when absent. */
  actionSummary: string | null
  /** Optional "Why"; null when absent or merely restating the diff. */
  why: string | null
}

interface FeedFieldRulesVinGroup {
  key: string
  vin: string
  fieldChanges: FeedFieldRulesFieldChangeView[]
  fieldCount: number
}

type FeedFieldRulesSearchStatus = 'idle' | 'loading' | 'notFound' | 'unavailable'

const FEED_FIELD_RULES_SEARCH_DEBOUNCE_MS = 250

const selectedFeedFieldRulesVin = ref('')
const feedFieldRulesTrailRecords = ref<PublishDecisionRecord[]>([])
const feedFieldRulesSearchStatus = ref<FeedFieldRulesSearchStatus>('idle')
/** Monotonic guard so a slow VIN-trail response can never overwrite a newer one. */
let feedFieldRulesSearchSeq = 0
let feedFieldRulesSearchDebounce: ReturnType<typeof setTimeout> | undefined

const feedFieldRulesSearchActive = computed(
  () => isFeedFieldRulesDecisionBrowse.value && stageVinSearch.value.trim().length > 0,
)

/** Returns a trimmed, non-empty matched-rule summary string, or null. */
function trimmedSummaryOrNull(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

/** Build one field-change view from a reason; null when the reason is unusable. */
function buildFeedFieldRulesFieldChangeView(
  reason: PublishDecisionReason,
): FeedFieldRulesFieldChangeView | null {
  const fieldDisplay = publishFlowFeedFieldRuleFieldLabel(reason)
  if (fieldDisplay.length === 0) return null
  const before = publishFlowFeedFieldRuleValueCopy(reason.beforeValue)
  const after = publishFlowFeedFieldRuleValueCopy(reason.afterValue)
  const rawSource = typeof reason.sourceAfter === 'string' ? reason.sourceAfter.trim() : ''
  const matchedRule = reason.matchedRule
  return {
    field: typeof reason.field === 'string' ? reason.field : '',
    fieldDisplay,
    changeDisplay: `${before} → ${after}`,
    feedSource: rawSource.length > 0 ? formatFeedSource(rawSource) : '',
    ruleLabel: publishFlowFeedFieldRuleActionLabel(reason),
    conditionSummary: trimmedSummaryOrNull(matchedRule?.conditionSummary),
    actionSummary: trimmedSummaryOrNull(matchedRule?.actionSummary),
    why: publishFlowFeedFieldRuleWhyCopy({
      explanation: reason.explanation,
      label: fieldDisplay,
      source: rawSource,
    }),
  }
}

function validFeedFieldRulesChanges(
  reasons: PublishDecisionRecord['reasons'],
): FeedFieldRulesFieldChangeView[] {
  if (!Array.isArray(reasons)) return []
  const out: FeedFieldRulesFieldChangeView[] = []
  for (const entry of reasons) {
    if (!entry || typeof entry !== 'object') continue
    const view = buildFeedFieldRulesFieldChangeView(entry as PublishDecisionReason)
    if (view) out.push(view)
  }
  return out
}

/**
 * Records feeding the feedFieldRules master/detail: the loaded browse pages
 * plus any VIN-trail hit (deduped by VIN, browse first). Both carry full
 * `reasons[]`, so the detail panel renders identically regardless of source.
 */
const feedFieldRulesSourceRecords = computed<PublishDecisionRecord[]>(() => {
  if (!isFeedFieldRulesDecisionBrowse.value) return []
  const browse = activeBrowserRowsForDisplay.value
  const trail = feedFieldRulesTrailRecords.value
  if (trail.length === 0) return browse
  const seen = new Set<string>()
  const merged: PublishDecisionRecord[] = []
  for (const record of [...browse, ...trail]) {
    const vin = String(record?.vin || '').trim().toUpperCase()
    if (!vin || seen.has(vin)) continue
    seen.add(vin)
    merged.push(record)
  }
  return merged
})

const feedFieldRulesAllGroups = computed<FeedFieldRulesVinGroup[]>(() => {
  if (!isFeedFieldRulesDecisionBrowse.value) return []
  const byVin = new Map<string, FeedFieldRulesVinGroup>()
  feedFieldRulesSourceRecords.value.forEach((record, idx) => {
    const vin = String(record.vin || '').trim()
    if (!vin) return
    const changes = validFeedFieldRulesChanges(record.reasons)
    const existing = byVin.get(vin)
    if (existing) {
      existing.fieldChanges.push(...changes)
      existing.fieldCount = existing.fieldChanges.length
      return
    }
    byVin.set(vin, { key: `${vin}:${idx}`, vin, fieldChanges: changes, fieldCount: changes.length })
  })
  return Array.from(byVin.values())
})

const feedFieldRulesBrowseGroups = computed<FeedFieldRulesVinGroup[]>(() => {
  const all = feedFieldRulesAllGroups.value
  const q = stageVinSearch.value.trim().toUpperCase()
  if (!q) return all
  return all.filter((group) => group.vin.toUpperCase().includes(q))
})

const selectedFeedFieldRulesGroup = computed<FeedFieldRulesVinGroup | null>(() => {
  if (!selectedFeedFieldRulesVin.value) return null
  return (
    feedFieldRulesBrowseGroups.value.find(
      (group) => group.vin === selectedFeedFieldRulesVin.value,
    ) ?? null
  )
})

const selectedFeedFieldRulesFieldChanges = computed<FeedFieldRulesFieldChangeView[]>(
  () => selectedFeedFieldRulesGroup.value?.fieldChanges ?? [],
)

/** True when a feedFieldRules search yielded nothing to show in the list. */
const feedFieldRulesSearchEmpty = computed(
  () => feedFieldRulesSearchActive.value && feedFieldRulesBrowseGroups.value.length === 0,
)

function reconcileSelectedFeedFieldRulesVin(): void {
  if (!isFeedFieldRulesDecisionBrowse.value) {
    selectedFeedFieldRulesVin.value = ''
    return
  }
  const groups = feedFieldRulesBrowseGroups.value
  if (groups.length === 0) {
    selectedFeedFieldRulesVin.value = ''
    return
  }
  if (groups.some((group) => group.vin === selectedFeedFieldRulesVin.value)) return
  selectedFeedFieldRulesVin.value = groups[0].vin
}

/** Whether a loaded browse VIN already matches the term (no network needed). */
function feedFieldRulesLoadedHasMatch(term: string): boolean {
  const q = term.trim().toUpperCase()
  if (!q) return false
  return activeBrowserRowsForDisplay.value.some((record) =>
    String(record?.vin || '').toUpperCase().includes(q),
  )
}

/** Reset feedFieldRules VIN-trail search back to browse mode. */
function resetFeedFieldRulesSearch(): void {
  if (feedFieldRulesSearchDebounce) {
    clearTimeout(feedFieldRulesSearchDebounce)
    feedFieldRulesSearchDebounce = undefined
  }
  feedFieldRulesSearchSeq += 1
  feedFieldRulesSearchStatus.value = 'idle'
  feedFieldRulesTrailRecords.value = []
}

/**
 * Resolve a single VIN's feed field rule records via the cross-stage VIN trail
 * (the backend scans the WHOLE artifact, not just the loaded page). A found VIN
 * is merged into the list; a found-run-but-absent VIN reads as "not found".
 */
async function runFeedFieldRulesVinSearch(term: string): Promise<void> {
  const seq = (feedFieldRulesSearchSeq += 1)
  feedFieldRulesSearchStatus.value = 'loading'
  const result = await tryGetPublishFlowVinTrail({
    endpoint: props.endpoint,
    jazelId: props.jazelId,
    correlationId: props.correlationId,
    sessionToken: props.sessionToken,
    vin: term,
  })
  if (seq !== feedFieldRulesSearchSeq) return

  if (result.status === 'ok') {
    const records = result.data.stages?.feedFieldRules?.records
    const list = Array.isArray(records)
      ? records.filter((record): record is PublishDecisionRecord =>
          Boolean(record) && typeof record.vin === 'string' && record.vin.length > 0,
        )
      : []
    feedFieldRulesTrailRecords.value = list
    feedFieldRulesSearchStatus.value = list.length > 0 ? 'idle' : 'notFound'
    return
  }
  if (result.status === 'notFound') {
    feedFieldRulesTrailRecords.value = []
    feedFieldRulesSearchStatus.value = 'notFound'
    return
  }
  feedFieldRulesTrailRecords.value = []
  feedFieldRulesSearchStatus.value = 'unavailable'
}

function buildFieldMappingFieldChangeView(
  reason: PublishDecisionReason,
  record: PublishDecisionRecord,
): FieldMappingFieldChangeView {
  const field = reason.field ?? ''
  const beforeRaw = reason.beforeValue
  const afterRaw = reason.afterValue
  const sourceLine = fieldMappingSourceLine(reason, record)
  const explanationRaw = typeof reason.explanation === 'string' ? reason.explanation : ''
  const beforeDisplay = formatFieldMappingValue(field, beforeRaw, 'before')
  const afterDisplay = formatFieldMappingValue(field, afterRaw, 'after')

  return {
    field,
    fieldDisplay: friendlyFieldMappingFieldLabel(field),
    beforeDisplay,
    afterDisplay,
    changeDisplay: `${beforeDisplay} → ${afterDisplay}`,
    sourceLines: sourceLine,
    explanation: friendlyFieldMappingExplanation({
      field,
      fieldDisplay: friendlyFieldMappingFieldLabel(field),
      beforeDisplay,
      afterDisplay,
      beforeRaw,
      afterRaw,
      explanationRaw,
    }),
  }
}

function fieldMappingSourceLine(
  reason: PublishDecisionReason,
  record: PublishDecisionRecord,
): FieldMappingSourceLine[] {
  const previousSource = friendlyFieldMappingSource(
    firstFieldMappingSourceValue(reason, record, [
      'sourceBefore',
      'beforeSource',
      'previousSource',
      'previousValueSource',
      'beforeValueSource',
    ]),
  )
  const newSource = friendlyFieldMappingSource(
    firstFieldMappingSourceValue(reason, record, [
      'sourceAfter',
      'afterSource',
      'newSource',
      'newValueSource',
      'afterValueSource',
      'mappedFrom',
      'fieldSource',
      'valueSource',
    ]),
  )

  if (previousSource && newSource) {
    if (previousSource === newSource) return [{ label: 'Source', display: newSource }]
    return [
      { label: 'Before source', display: previousSource },
      { label: 'After source', display: newSource },
    ]
  }

  if (newSource) return [{ label: 'Source', display: newSource }]
  if (previousSource) return [{ label: 'Previous source', display: previousSource }]

  const feedSource = friendlyFieldMappingFeedSource(reason, record)
  return feedSource ? [{ label: 'Feed source', display: feedSource }] : []
}

function firstFieldMappingSourceValue(
  reason: PublishDecisionReason,
  record: PublishDecisionRecord,
  keys: string[],
): unknown {
  const reasonRecord = reason as Record<string, unknown>
  const reasonProvenance = objectRecord(reasonRecord.provenance)
  const recordRecord = record as unknown as Record<string, unknown>
  const recordProvenance = objectRecord(record.provenance)

  return (
    firstKnownSourceValue(reasonRecord, keys) ??
    firstKnownSourceValue(reasonProvenance, keys, { allowGenericProducerKeys: false }) ??
    firstKnownSourceValue(recordRecord, keys) ??
    firstKnownSourceValue(recordProvenance, keys, { allowGenericProducerKeys: false })
  )
}

function firstKnownSourceValue(
  source: Record<string, unknown> | null,
  keys: string[],
  options: { allowGenericProducerKeys?: boolean } = {},
): unknown {
  if (!source) return undefined
  for (const key of keys) {
    if (options.allowGenericProducerKeys === false && technicalProducerKeySet.has(key)) continue
    const value = source[key]
    if (sourceValueIsPresent(value)) return value
  }

  if (keys.includes('feedSource') && Array.isArray(source.feedSources) && source.feedSources.length === 1) {
    const [feedSource] = source.feedSources
    if (sourceValueIsPresent(feedSource)) return feedSource
  }

  return undefined
}

const technicalProducerKeySet = new Set(['source', 'producer', 'createdBy', 'writer'])

function friendlyFieldMappingFeedSource(
  reason: PublishDecisionReason,
  record: PublishDecisionRecord,
): string {
  const reasonRecord = reason as Record<string, unknown>
  const reasonProvenance = objectRecord(reasonRecord.provenance)
  const recordRecord = record as unknown as Record<string, unknown>
  const recordProvenance = objectRecord(record.provenance)

  const source =
    firstFeedSourceValue(reasonRecord) ??
    firstFeedSourceValue(reasonProvenance) ??
    firstFeedSourceValue(recordRecord) ??
    firstFeedSourceValue(recordProvenance)

  if (Array.isArray(source)) {
    const labels = source.map(friendlyFieldMappingSource).filter(Boolean)
    return Array.from(new Set(labels)).join(', ')
  }

  return friendlyFieldMappingSource(source)
}

function firstFeedSourceValue(source: Record<string, unknown> | null): unknown {
  if (!source) return undefined
  if (sourceValueIsPresent(source.feedSource)) return source.feedSource
  if (Array.isArray(source.feedSources)) {
    const validFeedSources = source.feedSources.filter(sourceValueIsPresent)
    if (validFeedSources.length > 0) return validFeedSources
  }
  return undefined
}

function sourceValueIsPresent(value: unknown): boolean {
  if (typeof value === 'string') return friendlyFieldMappingSource(value).length > 0
  return false
}

function objectRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

function friendlyFieldMappingFieldLabel(field: string): string {
  const normalized = field.trim()
  const normalizedKey = normalized.replace(/[-\s]/g, '_').toLowerCase()
  const knownLabels: Record<string, string> = {
    tags: 'Tags',
    price: 'Price',
    stock_number: 'Stock number',
    condition: 'Condition',
    feedsource: 'Feed source',
    feed_source: 'Feed source',
  }
  return knownLabels[normalizedKey] ?? knownLabels[normalized] ?? titleCaseIdentifier(normalized)
}

function formatFieldMappingValue(
  field: string,
  value: unknown,
  position: 'before' | 'after',
): string {
  if (isMissingFieldMappingValue(value)) {
    return isTagsField(field) && position === 'after' ? 'No tags' : 'No value'
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return isTagsField(field) ? 'No tags' : 'None'

    const displayValues = value
      .map((entry) => formatFieldMappingArrayEntry(field, entry))
      .filter(Boolean)

    return displayValues.length > 0 ? displayValues.join(', ') : 'Updated'
  }

  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (typeof value === 'number') return String(value)
  if (typeof value === 'string') return value.trim() === '' ? 'No value' : value

  return formatFieldMappingObjectValue(value)
}

function formatFieldMappingArrayEntry(field: string, value: unknown): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (typeof value === 'number') return String(value)
  if (typeof value === 'string') {
    if (isRawVehicleIdentifierField(field)) return value
    return titleCaseIdentifier(value)
  }
  return formatFieldMappingObjectValue(value)
}

function formatFieldMappingObjectValue(value: unknown): string {
  if (!value || typeof value !== 'object') return 'Updated'
  const record = value as Record<string, unknown>
  const directLabel = firstStringValue(record, ['label', 'name', 'title', 'value'])
  if (directLabel) return directLabel

  const entries = Object.entries(record)
    .filter(([, entryValue]) => typeof entryValue === 'string' || typeof entryValue === 'number')
    .slice(0, 2)
    .map(([key, entryValue]) => `${titleCaseIdentifier(key)}: ${String(entryValue)}`)

  return entries.length > 0 ? entries.join(', ') : 'Updated'
}

function firstStringValue(record: Record<string, unknown>, keys: string[]): string {
  for (const key of keys) {
    const value = record[key]
    if (typeof value === 'string' && value.trim()) return value.trim()
  }
  return ''
}

function friendlyFieldMappingSource(value: unknown): string {
  if (typeof value !== 'string') return ''
  const trimmed = value.trim()
  if (!trimmed || trimmed === EMPTY_VALUE_SENTINEL || trimmed === '-' || trimmed === '—') return ''

  const normalized = trimmed.replace(/[-_\s]/g, '').toLowerCase()
  if (isTechnicalFieldMappingProducer(normalized)) return ''

  const knownSources: Record<string, string> = {
    fdds2: 'FDDS',
    vauto: 'vAuto',
    feed: 'Feed mapping',
    feedmapping: 'Feed mapping',
    fieldmapping: 'Feed mapping',
    manual: 'Manual',
    system: 'System',
    ai: 'AI',
    chrome: 'Data Hub',
    datahub: 'Data Hub',
    homenet: 'HomeNet',
    homenetapi: 'HomeNet API',
    unknown: '',
  }

  return knownSources[normalized] ?? titleCaseIdentifier(trimmed)
}

function isTechnicalFieldMappingProducer(normalized: string): boolean {
  return [
    'srps3creator',
    'publishflow',
    'fieldmappingdecisionbuilder',
    'fieldmappingbuilder',
    'decisionbuilder',
    'artifactwriter',
    'publishflowobservability',
  ].includes(normalized)
}

function friendlyFieldMappingExplanation(input: {
  field: string
  fieldDisplay: string
  beforeDisplay: string
  afterDisplay: string
  beforeRaw: unknown
  afterRaw: unknown
  explanationRaw: string
}): string {
  if (isTagsField(input.field) && hasFieldMappingValue(input.beforeRaw) && !hasFieldMappingValue(input.afterRaw)) {
    return 'The latest feed mapping removed this tag.'
  }

  const trimmed = input.explanationRaw.trim()
  if (isMeaningfulFieldMappingExplanation(trimmed, input)) return trimmed

  return ''
}

function isMeaningfulFieldMappingExplanation(
  value: string,
  input: {
    field: string
    fieldDisplay: string
    beforeDisplay: string
    afterDisplay: string
  },
): boolean {
  if (!value) return false
  if (/[{}[\]"]/.test(value)) return false
  if (!/[a-zA-Z]/.test(value)) return false

  const normalized = normalizeFieldMappingExplanation(value)
  if (!normalized) return false

  const fieldAliases = [
    input.field,
    input.fieldDisplay,
    titleCaseIdentifier(input.field),
    input.field.replace(/[_-]+/g, ' '),
  ]
    .map(normalizeFieldMappingExplanation)
    .filter(Boolean)

  if (fieldAliases.some((field) => normalized === `${field} changed`)) return false

  const changedFromPattern = /^(.+?) changed from (.+?) to (.+)$/
  const changedFromMatch = normalized.match(changedFromPattern)
  if (changedFromMatch) {
    const [, rawField] = changedFromMatch
    const explanationField = normalizeFieldMappingExplanation(rawField)
    // Even when the literal values differ (for example `empty` vs `No value`),
    // a plain same-field "changed from ... to ..." sentence adds no context beyond the diff line.
    return !fieldAliases.includes(explanationField)
  }

  return true
}

function normalizeFieldMappingExplanation(value: string): string {
  return value
    .replace(/\.$/, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
}

function hasFieldMappingValue(value: unknown): boolean {
  return !isMissingFieldMappingValue(value) && (!Array.isArray(value) || value.length > 0)
}

function isMissingFieldMappingValue(value: unknown): boolean {
  return value === null || value === undefined || (typeof value === 'string' && value.trim() === '')
}

function isTagsField(field: string): boolean {
  return field.trim().toLowerCase() === 'tags'
}

function isRawVehicleIdentifierField(field: string): boolean {
  const key = field.trim().replace(/[-\s]/g, '_').toLowerCase()
  return key === 'vin' || key === 'stock_number'
}

function titleCaseIdentifier(value: string): string {
  const words = value
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean)

  return words
    .map((word) => {
      if (/^[A-Z0-9]+$/.test(word) && word.length <= 3) return word
      return `${word.charAt(0).toUpperCase()}${word.slice(1).toLowerCase()}`
    })
    .join(' ')
}

function coerceFieldMappingDisplay(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  try {
    return JSON.stringify(value)
  } catch {
    return ''
  }
}

watch(
  [
    () => isFieldMappingDecisionBrowse.value,
    () => fieldMappingBrowseGroups.value.map((group) => group.vin).join('\u0000'),
  ],
  () => reconcileSelectedFieldMappingVin(),
  { immediate: true, flush: 'post' },
)

// =============================================================================
// Vehicle Groups decisions browse (stage-scoped "View groups").
//
// The groups decision records already carry current membership
// (`after.segments`), prior membership (`before.segments`, omitted when the
// prior read failed), a `status` diff, and causation `reasons[]`. The
// stage-scoped browse renders a master/detail view (mirroring fieldMapping):
// a light VIN list with current-group badges on the left, and the selected
// VIN's current/previous groups, change flag, reasons, and grouping inputs on
// the right. The cross-stage VIN trail still uses <GroupsDecisionTrailRow>.
// =============================================================================

interface GroupBadge {
  id: string
  label: string
}

interface GroupsReasonView {
  explanation: string
  /** Derived from the explanation prefix; drives no copy, only display intent. */
  action: 'added' | 'removed' | 'neutral'
  /**
   * True when the reason carries captured criterion causation (Phase 10.1
   * add-reason enrichment: a real non-membership `field` plus an operator,
   * expected, or actual value). Removed-group reasons never carry this — the
   * artifact does not capture why a vehicle stopped matching.
   */
  hasCriterion: boolean
  /** e.g. `certified_by_manufacturer equals true` (only when hasCriterion). */
  conditionText?: string
  /** The vehicle's actual value for the matched criterion (only when present). */
  actualText?: string
  /** Rule/config label when the criterion fully explains the rule. */
  ruleName?: string
  /**
   * True for a removed-group reason with no captured criterion metadata. Drives
   * an honest "detailed criteria were not captured" note instead of guessing.
   */
  missingRemovalCriteria: boolean
}

interface GroupsInputView {
  field: string
  value: string
}

interface GroupsVinRow {
  key: string
  vin: string
  status: string
  changed: boolean
  /** Prior membership unavailable for this run → only additions are trustworthy. */
  priorReadFailed: boolean
  currentGroups: GroupBadge[]
  previousGroups: GroupBadge[]
  /** Per-VIN display: specific groups only (All Vehicles filtered out). */
  displayCurrentGroups: GroupBadge[]
  displayPreviousGroups: GroupBadge[]
  /** True when the VIN is only in the generic All Vehicles bucket. */
  displayCurrentNoSpecificGroup: boolean
  displayPreviousNoSpecificGroup: boolean
  reasons: GroupsReasonView[]
  /** True when ≥1 removed-group reason lacked captured criterion metadata. */
  showRemovedCriteriaNote: boolean
  inputs: GroupsInputView[]
}

const selectedGroupsVin = ref('')

/**
 * Defensive extraction of the writer's `{ segments: { id, name? }[] }` shape
 * from a record's `before` / `after` (typed `unknown` in the shared contract).
 * Anything unexpected coerces to `[]`. The display label prefers the segment
 * name and falls back to the id.
 */
function extractGroupBadges(value: unknown): GroupBadge[] {
  if (!value || typeof value !== 'object') return []
  const segments = (value as { segments?: unknown }).segments
  if (!Array.isArray(segments)) return []
  const out: GroupBadge[] = []
  const seen = new Set<string>()
  for (const entry of segments) {
    if (!entry || typeof entry !== 'object') continue
    const id = (entry as { id?: unknown }).id
    if (typeof id !== 'string' || id.length === 0 || seen.has(id)) continue
    seen.add(id)
    const name = (entry as { name?: unknown }).name
    out.push({ id, label: typeof name === 'string' && name.length > 0 ? name : id })
  }
  return out
}

/** Normalize a segment id/name for generic All Vehicles matching. */
function normalizeGroupToken(value: string): string {
  return value.trim().toLowerCase().replace(/_/g, ' ').replace(/\s+/g, ' ')
}

/**
 * True when a group badge represents the catch-all "All Vehicles" bucket.
 * Matched defensively on id or display label (e.g. `all`, `all_vehicles`,
 * `All Vehicles`). Used ONLY for per-VIN badge rendering — the distribution
 * summary card intentionally keeps All Vehicles visible.
 */
function isGenericAllVehiclesGroup(group: GroupBadge): boolean {
  for (const token of [group.id, group.label]) {
    if (typeof token !== 'string' || token.length === 0) continue
    const normalized = normalizeGroupToken(token)
    if (normalized === 'all' || normalized === 'all vehicles') return true
  }
  return false
}

/** Per-VIN groups with generic All Vehicles removed. */
function specificGroups(groups: GroupBadge[]): GroupBadge[] {
  return groups.filter((group) => !isGenericAllVehiclesGroup(group))
}

/**
 * Per-VIN group badges for the dialog. Returns specific groups when present;
 * when the VIN is only in All Vehicles, signals the muted fallback copy.
 */
function displayGroupsForVin(groups: GroupBadge[]): {
  badges: GroupBadge[]
  noSpecificGroup: boolean
} {
  const badges = specificGroups(groups)
  if (badges.length > 0) return { badges, noSpecificGroup: false }
  if (groups.some((group) => isGenericAllVehiclesGroup(group))) {
    return { badges: [], noSpecificGroup: true }
  }
  return { badges: [], noSpecificGroup: false }
}

/** Dealer-readable phrasing for the evaluator's operator tokens. */
const GROUPS_OPERATOR_LABELS: Record<string, string> = {
  equals: 'equals',
  not_equals: 'does not equal',
  contains: 'contains',
  not_contains: 'does not contain',
  greater_than: 'is greater than',
  less_than: 'is less than',
  greater_than_or_equal: 'is at least',
  less_than_or_equal: 'is at most',
  exists: 'is present',
  not_exists: 'is missing',
}

function formatGroupsOperator(operator: string): string {
  const key = operator.trim().toLowerCase()
  return GROUPS_OPERATOR_LABELS[key] ?? key.replace(/_/g, ' ')
}

/** Added/Removed intent derived from the writer's explanation prefix. */
function deriveGroupsReasonAction(explanation: string): 'added' | 'removed' | 'neutral' {
  const normalized = explanation.trim().toLowerCase()
  if (normalized.startsWith('added to')) return 'added'
  if (normalized.startsWith('removed from')) return 'removed'
  return 'neutral'
}

/**
 * Build the per-reason view model. The groups artifact enriches ADD reasons
 * (Phase 10.1) with `field`/`operator`/`expectedValue`/`afterValue`/`matched`,
 * but REMOVED reasons carry only `{ field: 'segments', explanation, beforeValue }`
 * (Guardrail #8 — removed-segment causation is not captured). We render the
 * captured criterion when present and an honest "not captured" note for removals
 * that lack it. No criterion is ever invented.
 */
function validGroupsReasons(reasons: PublishDecisionRecord['reasons']): GroupsReasonView[] {
  if (!Array.isArray(reasons)) return []
  const out: GroupsReasonView[] = []
  for (const reason of reasons) {
    if (!reason || typeof reason !== 'object') continue
    const explanationRaw = (reason as { explanation?: unknown }).explanation
    if (typeof explanationRaw !== 'string' || explanationRaw.trim().length === 0) continue
    const explanation = explanationRaw
    const action = deriveGroupsReasonAction(explanation)

    const fieldRaw = (reason as { field?: unknown }).field
    // `segments` is the membership-diff marker, not a real criterion field.
    const field =
      typeof fieldRaw === 'string' && fieldRaw.length > 0 && fieldRaw !== 'segments'
        ? fieldRaw
        : undefined
    const operatorRaw = (reason as { operator?: unknown }).operator
    const operator =
      typeof operatorRaw === 'string' && operatorRaw.length > 0 ? operatorRaw : undefined
    const expectedRaw = (reason as { expectedValue?: unknown }).expectedValue
    const hasExpected = expectedRaw !== undefined
    const actualRaw = (reason as { afterValue?: unknown }).afterValue
    const hasActual = actualRaw !== undefined

    const hasCriterion = Boolean(field) && (Boolean(operator) || hasExpected || hasActual)

    let conditionText: string | undefined
    if (field && operator && hasExpected) {
      conditionText = `${field} ${formatGroupsOperator(operator)} ${coerceFieldMappingDisplay(expectedRaw)}`
    } else if (field && operator) {
      conditionText = `${field} ${formatGroupsOperator(operator)}`
    } else if (field && hasExpected) {
      conditionText = `${field}: ${coerceFieldMappingDisplay(expectedRaw)}`
    } else if (field) {
      conditionText = field
    }

    const ruleNameRaw =
      (reason as { ruleName?: unknown }).ruleName ?? (reason as { configLabel?: unknown }).configLabel
    const ruleName =
      typeof ruleNameRaw === 'string' && ruleNameRaw.length > 0 ? ruleNameRaw : undefined

    out.push({
      explanation,
      action,
      hasCriterion,
      ...(hasCriterion && conditionText ? { conditionText } : {}),
      ...(hasCriterion && hasActual ? { actualText: coerceFieldMappingDisplay(actualRaw) } : {}),
      ...(hasCriterion && ruleName ? { ruleName } : {}),
      missingRemovalCriteria: action === 'removed' && !hasCriterion,
    })
  }
  return out
}

/**
 * Field/source inputs the grouping engine matched on, derived from causation
 * reasons (`matched === true`). Each carries the field and the vehicle's actual
 * value (`afterValue`). De-duplicated by field+value; segment-diff reasons
 * (`field === 'segments'`) are skipped since they describe membership, not the
 * input that drove it.
 */
function buildGroupsInputs(reasons: PublishDecisionRecord['reasons']): GroupsInputView[] {
  if (!Array.isArray(reasons)) return []
  const out: GroupsInputView[] = []
  const seen = new Set<string>()
  for (const reason of reasons) {
    if (!reason || typeof reason !== 'object') continue
    if ((reason as { matched?: unknown }).matched !== true) continue
    const field = (reason as { field?: unknown }).field
    if (typeof field !== 'string' || field.length === 0 || field === 'segments') continue
    const value = coerceFieldMappingDisplay((reason as { afterValue?: unknown }).afterValue)
    const key = `${field}\u0000${value}`
    if (seen.has(key)) continue
    seen.add(key)
    out.push({ field, value })
  }
  return out
}

function segmentIdsFromUnknown(value: unknown): string[] | null {
  if (!value || typeof value !== 'object') return null
  const segments = (value as { segments?: unknown }).segments
  if (!Array.isArray(segments)) return null
  const ids = segments
    .map((entry) => {
      if (!entry || typeof entry !== 'object') return ''
      const id = (entry as { id?: unknown }).id
      return typeof id === 'string' ? id.trim() : ''
    })
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b))
  return ids
}

function hasNonEmptyArrayField(record: PublishDecisionRecord, fields: string[]): boolean {
  const raw = record as unknown as Record<string, unknown>
  return fields.some((field) => Array.isArray(raw[field]) && (raw[field] as unknown[]).length > 0)
}

function isGroupMembershipChanged(record: PublishDecisionRecord): boolean {
  if (record.status === 'changed') return true
  if ((record as unknown as { changed?: unknown }).changed === true) return true
  if (
    hasNonEmptyArrayField(record, [
      'addedGroups',
      'removedGroups',
      'addedSegments',
      'removedSegments',
    ])
  ) {
    return true
  }

  const beforeIds = segmentIdsFromUnknown(record.before)
  const afterIds = segmentIdsFromUnknown(record.after)
  if (!beforeIds || !afterIds) return false
  if (beforeIds.length !== afterIds.length) return true
  return beforeIds.some((id, idx) => id !== afterIds[idx])
}

function buildGroupsVinRow(record: PublishDecisionRecord, idx: number, vin: string): GroupsVinRow {
  const priorReadFailed = record.provenance?.priorReadFailed === true
  const currentGroups = extractGroupBadges(record.after)
  const previousGroups = priorReadFailed ? [] : extractGroupBadges(record.before)
  const currentDisplay = displayGroupsForVin(currentGroups)
  const previousDisplay = displayGroupsForVin(previousGroups)
  const status = typeof record.status === 'string' ? record.status : ''
  const reasons = validGroupsReasons(record.reasons)
  return {
    key: `${vin}:${idx}`,
    vin,
    status,
    changed: isGroupMembershipChanged(record),
    priorReadFailed,
    currentGroups,
    previousGroups,
    displayCurrentGroups: currentDisplay.badges,
    displayCurrentNoSpecificGroup: currentDisplay.noSpecificGroup,
    displayPreviousGroups: previousDisplay.badges,
    displayPreviousNoSpecificGroup: previousDisplay.noSpecificGroup,
    reasons,
    showRemovedCriteriaNote: reasons.some((reason) => reason.missingRemovalCriteria),
    inputs: buildGroupsInputs(record.reasons),
  }
}

const groupsAllRows = computed<GroupsVinRow[]>(() => {
  if (!isGroupsDecisionBrowse.value) return []
  const byVin = new Map<string, GroupsVinRow>()
  decisionSearchSourceRecords.value.forEach((record, idx) => {
    const vin = String(record.vin || '').trim()
    // groups emits one record per VIN; keep the first if a dup ever appears.
    if (!vin || byVin.has(vin)) return
    byVin.set(vin, buildGroupsVinRow(record, idx, vin))
  })
  return Array.from(byVin.values())
})

// Both filters are resolved SERVER-SIDE (before pagination):
//   - default browse sends `changedOnly=true` → these rows are already the
//     changed subset;
//   - search sends `search=<term>` (no `changedOnly`) → these rows are the
//     matched subset across ALL evaluated vehicles.
// So the left list is exactly what the server returned; no client filtering.
const groupsBrowseRows = computed<GroupsVinRow[]>(() => groupsAllRows.value)

const selectedGroupsRow = computed<GroupsVinRow | null>(() => {
  if (!selectedGroupsVin.value) return null
  return groupsBrowseRows.value.find((row) => row.vin === selectedGroupsVin.value) ?? null
})

/**
 * Total for the heading. In browse mode the server filtered to changed
 * records and reports the full changed count via the page's `totalRecords`
 * (which equals `distribution.changedCount`); we prefer it so the heading
 * reads `Vehicle Groups (23)` even before every page is loaded. In search
 * mode it reflects the matched rows currently loaded.
 */
const groupsHeadingTotal = computed(() => {
  if (!isGroupsDecisionBrowse.value) return 0
  if (decisionSearchActive.value) return groupsBrowseRows.value.length
  const pages = store.activeBrowserSlice?.pages ?? []
  const firstWithTotal = pages.find(
    (page) => typeof (page as PublishStageDecisionsPage).totalRecords === 'number',
  ) as PublishStageDecisionsPage | undefined
  return firstWithTotal?.totalRecords ?? groupsBrowseRows.value.length
})

/** Rows actually loaded into the left list (browse page(s) or search matches). */
const groupsHeadingShown = computed(() =>
  isGroupsDecisionBrowse.value ? groupsBrowseRows.value.length : 0,
)

/**
 * Zero-changed run: default (non-search) browse loaded successfully but the
 * server returned no changed records. The dialog then nudges the user toward
 * search instead of exposing an all-evaluated list.
 */
const showGroupsNoChangedNotice = computed(
  () =>
    isGroupsDecisionBrowse.value &&
    !decisionSearchActive.value &&
    store.activeBrowserSlice?.status === 'ok' &&
    groupsBrowseRows.value.length === 0,
)

function reconcileSelectedGroupsVin(): void {
  if (!isGroupsDecisionBrowse.value) {
    selectedGroupsVin.value = ''
    return
  }
  const rows = groupsBrowseRows.value
  if (rows.length === 0) {
    selectedGroupsVin.value = ''
    return
  }
  if (rows.some((row) => row.vin === selectedGroupsVin.value)) return
  selectedGroupsVin.value = rows[0].vin
}

watch(
  [
    () => isGroupsDecisionBrowse.value,
    () => groupsBrowseRows.value.map((row) => row.vin).join('\u0000'),
    () => decisionSearchActive.value,
  ],
  () => reconcileSelectedGroupsVin(),
  { immediate: true, flush: 'post' },
)

// =============================================================================
// Website Visibility (listingRules) decisions browse (stage-scoped "View
// details" from the Publish Rules Evaluation card).
//
// The listingRules decision records carry the resulting membership
// (`after.membership`: accepted/blocked), the prior membership
// (`before.membership`), a `status` diff, causation `reasons[]` (rule
// identity + human explanation), and feed provenance (`provenance.feedSource`
// / `feedSources`). Default browse shows ONLY vehicles that matched blocking
// rules (server `matchedBlockingOnly=true`); search spans ALL evaluated
// records (server `search=`, accepted included). The master/detail mirrors the
// Vehicle Groups browse: a light VIN list with a decision badge on the left,
// and the selected VIN's decision/rule/reason/feeds on the right.
// =============================================================================

const RUN_SUMMARY_VISIBILITY_HELPER =
  'Run Summary counts only live website changes from this publish. Vehicles ' +
  'already archived or not live are not counted again as Removed or Hidden.'

const SOURCE_AUTHORITY_HELPER =
  'Source authority checks known feed membership across feeds, not only the ' +
  'feed that triggered this publish.'

interface ListingRulesVinRow {
  key: string
  vin: string
  /** Resulting membership for this run: blocked (matched a blocking rule) vs accepted. */
  blocked: boolean
  /** `Matched blocking rule` / `Accepted` / `Evaluation error`. */
  decisionLabel: string
  /** Dealer-friendly rule label (source-authority copy rewritten), or ''. */
  ruleLabel: string
  /** True when the matched rule is a generated Feed Source Config source-authority rule. */
  isSourceAuthority: boolean
  /** Required feed for a source-authority rule (e.g. `FDDS2`), or ''. */
  requiredFeed: string
  /** Human-readable rule explanation, or ''. */
  reason: string
  /**
   * Dealer-readable "Why it matched" bullet lines built from captured reason
   * metadata (criterion field/operator/expected/actual, source-authority feed
   * context, meaningful explanations). Empty when no specific reason exists.
   */
  whyMatchedLines: string[]
  /** Feed provenance for this VIN (deduped), e.g. ['vAuto']. */
  feeds: string[]
  /** Derived, honest visibility outcome note, or '' when not determinable. */
  outcomeNote: string
}

const selectedListingRulesVin = ref('')

/** Read `accepted` / `blocked` / null from a record's before/after object. */
function readMembershipFlag(value: unknown): 'accepted' | 'blocked' | null {
  if (!value || typeof value !== 'object') return null
  const membership = (value as { membership?: unknown }).membership
  if (membership === 'accepted' || membership === 'blocked') return membership
  return null
}

/** Pretty feed-code casing for display (FDDS2, vAuto), mirroring metrics copy. */
function formatFeedCodeForDisplay(feed: string): string {
  const normalized = feed.trim()
  if (normalized.length === 0) return ''
  if (/^fdds\d+$/i.test(normalized)) return normalized.toUpperCase()
  if (normalized.toLowerCase() === 'vauto') return 'vAuto'
  return normalized
}

/** Deduped feed provenance from `provenance.feedSource` + optional `feedSources`. */
function extractListingRulesFeeds(record: PublishDecisionRecord): string[] {
  const out: string[] = []
  const seen = new Set<string>()
  const push = (raw: unknown) => {
    if (typeof raw !== 'string') return
    const display = formatFeedCodeForDisplay(raw)
    if (!display) return
    const key = display.toUpperCase()
    if (seen.has(key)) return
    seen.add(key)
    out.push(display)
  }
  const provenance = (record.provenance ?? {}) as Record<string, unknown>
  push(provenance.feedSource)
  const feedSources = (record as { feedSources?: unknown }).feedSources
  if (Array.isArray(feedSources)) feedSources.forEach(push)
  return out
}

/** Extract the required feed from a generated source-authority rule id/reason. */
function extractRequiredFeed(rawRuleId: string): string {
  const match = rawRuleId
    .replace(/^\[auto:feed-mapping]\s*/i, '')
    .match(/ must come from ([A-Za-z0-9_-]+)$/i)
  return match ? formatFeedCodeForDisplay(match[1] ?? '') : ''
}

/**
 * Honest visibility outcome note derived from before/after membership + status.
 * Returns '' when no confident note can be made (we never invent one).
 */
function deriveOutcomeNote(record: PublishDecisionRecord): string {
  const before = readMembershipFlag(record.before)
  const after = readMembershipFlag(record.after)
  if (before === 'accepted' && after === 'blocked') return 'Archived this run'
  if (before === 'blocked' && after === 'blocked') return 'Already archived / not live'
  if (before === 'accepted' && after === 'accepted') return 'Accepted, no visibility change'
  return ''
}

function buildListingRulesVinRow(
  record: PublishDecisionRecord,
  idx: number,
  vin: string,
): ListingRulesVinRow {
  const membership = readMembershipFlag(record.after)
  const blocked = membership === 'blocked'
  let decisionLabel = 'Evaluation error'
  if (membership === 'blocked') decisionLabel = 'Matched blocking rule'
  else if (membership === 'accepted') decisionLabel = 'Accepted'

  const reasons = Array.isArray(record.reasons) ? record.reasons : []
  const primary = reasons.length > 0 ? reasons[0] : undefined
  const afterReason = (record.after as { reason?: unknown } | undefined)?.reason
  const rawRuleId =
    (typeof primary?.ruleId === 'string' && primary.ruleId) ||
    (typeof afterReason === 'string' && afterReason) ||
    (typeof primary?.field === 'string' && primary.field) ||
    ''
  const configuredLabel =
    (typeof primary?.configLabel === 'string' && primary.configLabel.trim()) ||
    (typeof primary?.ruleName === 'string' && primary.ruleName.trim()) ||
    ''
  const isSourceAuthority = isGeneratedSourceAuthorityRuleLabel(rawRuleId)
  const ruleLabel =
    rawRuleId || configuredLabel
      ? formatPublishEligibilityDialogRuleLabel(rawRuleId, configuredLabel || undefined)
      : ''
  const requiredFeed = isSourceAuthority ? extractRequiredFeed(rawRuleId) : ''
  const formattedReason =
    typeof primary?.explanation === 'string'
      ? formatPublishEligibilityReasonExplanation(primary.explanation, rawRuleId)
      : ''

  const whyMatchedLines = blocked
    ? buildPublishEligibilityWhyMatchedLines({
        ruleId: rawRuleId,
        field: typeof primary?.field === 'string' ? primary.field : undefined,
        operator: typeof primary?.operator === 'string' ? primary.operator : undefined,
        expectedValue: primary?.expectedValue,
        actualValue: primary?.actualValue,
        afterValue: primary?.afterValue,
        sourceAfter: typeof primary?.sourceAfter === 'string' ? primary.sourceAfter : undefined,
        explanation: typeof primary?.explanation === 'string' ? primary.explanation : undefined,
        isSourceAuthority,
        requiredFeed,
      })
    : []

  // Blocked VINs always show a "Why it matched" answer: specific lines when
  // captured, otherwise the honest fallback (we never invent a reason).
  const reason = blocked
    ? formattedReason || publishEligibilityFallbackReason()
    : formattedReason

  return {
    key: `${vin}:${idx}`,
    vin,
    blocked,
    decisionLabel,
    ruleLabel,
    isSourceAuthority,
    requiredFeed,
    reason,
    whyMatchedLines,
    feeds: extractListingRulesFeeds(record),
    outcomeNote: deriveOutcomeNote(record),
  }
}

const listingRulesAllRows = computed<ListingRulesVinRow[]>(() => {
  if (!isListingRulesDecisionBrowse.value) return []
  const byVin = new Map<string, ListingRulesVinRow>()
  decisionSearchSourceRecords.value.forEach((record, idx) => {
    const vin = String(record.vin || '').trim()
    // listingRules emits one record per VIN; keep the first if a dup appears.
    if (!vin || byVin.has(vin)) return
    byVin.set(vin, buildListingRulesVinRow(record, idx, vin))
  })
  return Array.from(byVin.values())
})

// Both filters are resolved SERVER-SIDE (before pagination):
//   - default browse sends `matchedBlockingOnly=true` → these rows are already
//     the blocked subset;
//   - search sends `search=<term>` (no `matchedBlockingOnly`) → these rows are
//     the matched subset across ALL evaluated vehicles.
// So the left list is exactly what the server returned; no client filtering.
const listingRulesBrowseRows = computed<ListingRulesVinRow[]>(() => listingRulesAllRows.value)

const selectedListingRulesRow = computed<ListingRulesVinRow | null>(() => {
  if (!selectedListingRulesVin.value) return null
  return (
    listingRulesBrowseRows.value.find((row) => row.vin === selectedListingRulesVin.value) ?? null
  )
})

/**
 * Run-wide accepted/blocked breakdown. Computed server-side over the FULL
 * artifact and returned on every listingRules page, so the first loaded browse
 * page carries it. Read from the store slice so it survives an active search
 * (whose response carries the same whole-run value).
 */
const listingRulesVisibility = computed<PublishVisibilitySummary | null>(() => {
  if (!isListingRulesDecisionBrowse.value) return null
  const pages = store.activeBrowserSlice?.pages ?? []
  for (const page of pages) {
    const visibility = (page as PublishStageDecisionsPage).visibility
    if (visibility) return visibility
  }
  return null
})

/**
 * Authoritative publish-rule totals from flow metrics, when provided and
 * complete. Preferred over the per-VIN decision page's `visibility`.
 */
const authoritativeListingRulesVisibility = computed<PublishVisibilitySummary | null>(() => {
  const a = props.authoritativeVisibility
  if (
    !a ||
    typeof a.evaluated !== 'number' ||
    typeof a.accepted !== 'number' ||
    typeof a.blocked !== 'number'
  ) {
    return null
  }
  return { evaluated: a.evaluated, accepted: a.accepted, blocked: a.blocked }
})

/** Authoritative-first visibility for the listingRules summary box. */
const effectiveListingRulesVisibility = computed<PublishVisibilitySummary | null>(
  () => authoritativeListingRulesVisibility.value ?? listingRulesVisibility.value,
)

/** Authoritative blocked count when known (null when not provided). */
const authoritativeBlockedCount = computed<number | null>(() => {
  const v = authoritativeListingRulesVisibility.value
  return v ? v.blocked : null
})

const listingRulesSummaryLines = computed<string[]>(() => {
  const visibility = effectiveListingRulesVisibility.value
  if (!visibility) return []
  const plural = (n: number, noun: string) => `${n} ${n === 1 ? noun : `${noun}s`}`
  const blockedLine =
    visibility.blocked === 1 ? '1 matched blocking rule' : `${visibility.blocked} matched blocking rules`
  return [
    `${plural(visibility.evaluated, 'vehicle')} evaluated`,
    `${visibility.accepted} accepted`,
    blockedLine,
  ]
})

/**
 * Zero-matched-blocking run: default (non-search) browse loaded successfully
 * but the server returned no blocked records. The dialog then nudges the user
 * toward search instead of exposing an all-accepted list.
 *
 * Only treat this as a genuine "no blocked" run when the authoritative count
 * agrees (or is unknown). When authoritative blockedCount > 0 but no decision
 * rows loaded, that is a missing-details case (see below), not a no-blocked run.
 */
const showListingRulesNoBlockedNotice = computed(
  () =>
    isListingRulesDecisionBrowse.value &&
    !decisionSearchActive.value &&
    store.activeBrowserSlice?.status === 'ok' &&
    listingRulesBrowseRows.value.length === 0 &&
    (authoritativeBlockedCount.value === null || authoritativeBlockedCount.value === 0),
)

/**
 * Authoritative totals report blocked VINs but no per-VIN decision rows were
 * captured for this run (deferred Phase 3 artifact issue). The modal must not
 * claim "No vehicles matched blocking rules"; instead it states the count and
 * that per-VIN details were not captured.
 */
const showListingRulesBlockedDetailsMissingNotice = computed(
  () =>
    isListingRulesDecisionBrowse.value &&
    !decisionSearchActive.value &&
    store.activeBrowserSlice?.status === 'ok' &&
    listingRulesBrowseRows.value.length === 0 &&
    (authoritativeBlockedCount.value ?? 0) > 0,
)

const listingRulesBlockedDetailsMissingMessage = computed(() => {
  const n = authoritativeBlockedCount.value ?? 0
  return `${n} ${
    n === 1 ? 'vehicle' : 'vehicles'
  } matched blocking rules, but per-VIN decision details were not captured for this run.`
})

// ---------------------------------------------------------------------------
// Phase 3 — Blocked-decisions drill-down panel.
//
// The dedicated blocked-decisions artifact answers "which VINs were blocked and
// why" with true VIN-level rule attribution (Tier 1 authoritative run records,
// Tier 2 SRP re-evaluation, Tier 3 unavailable). It covers already-blocked /
// archived VINs the per-VIN listingRules decision page misses, so it is the
// primary blocked-VIN surface whenever rows are available.
// ---------------------------------------------------------------------------

const blockedDecisionRows = computed<BlockedDecisionRecord[]>(() => store.blockedDecisionRows)
const hasBlockedDecisionRows = computed(() => blockedDecisionRows.value.length > 0)
const blockedDecisionsLoading = computed(() => store.blockedDecisions.status === 'loading')
const blockedDecisionsHasMore = computed(() => store.blockedDecisionsHasMore)
const blockedDecisionsTotal = computed(() => store.blockedDecisionsTotal)

/** Show the blocked-decisions panel for the listingRules section whenever it has rows (or is loading). */
const showBlockedDecisionsPanel = computed(
  () =>
    isListingRulesDecisionBrowse.value &&
    !decisionSearchActive.value &&
    (hasBlockedDecisionRows.value || blockedDecisionsLoading.value),
)

/**
 * Suppress the legacy "per-VIN details were not captured" notice when the
 * blocked-decisions artifact provides rows (or is still loading) — that artifact
 * is exactly the recovery for the previously-missing detail.
 */
const showListingRulesBlockedDetailsMissingNoticeEffective = computed(
  () =>
    showListingRulesBlockedDetailsMissingNotice.value &&
    !hasBlockedDecisionRows.value &&
    !blockedDecisionsLoading.value,
)

function blockedDecisionAttributionLabelFor(row: BlockedDecisionRecord): string {
  return blockedDecisionAttributionLabel(row)
}
function blockedDecisionClassificationLabelFor(row: BlockedDecisionRecord): string {
  return blockedDecisionClassificationLabel(row.classification)
}
function blockedDecisionReasonTextFor(row: BlockedDecisionRecord): string {
  return blockedDecisionReasonText(row)
}

async function loadMoreBlockedDecisions(): Promise<void> {
  await store.loadBlockedDecisionsNextPage({
    endpoint: props.endpoint,
    jazelId: props.jazelId,
    correlationId: props.correlationId,
    sessionToken: props.sessionToken,
  })
}

/** True when the source-authority helper should accompany the selected detail. */
const showSourceAuthorityHelper = computed(
  () => Boolean(selectedListingRulesRow.value?.isSourceAuthority),
)

function reconcileSelectedListingRulesVin(): void {
  if (!isListingRulesDecisionBrowse.value) {
    selectedListingRulesVin.value = ''
    return
  }
  const rows = listingRulesBrowseRows.value
  if (rows.length === 0) {
    selectedListingRulesVin.value = ''
    return
  }
  if (rows.some((row) => row.vin === selectedListingRulesVin.value)) return
  selectedListingRulesVin.value = rows[0].vin
}

watch(
  [
    () => isListingRulesDecisionBrowse.value,
    () => listingRulesBrowseRows.value.map((row) => row.vin).join('\u0000'),
    () => decisionSearchActive.value,
  ],
  () => reconcileSelectedListingRulesVin(),
  { immediate: true, flush: 'post' },
)

watch(
  [
    () => isFeedFieldRulesDecisionBrowse.value,
    () => feedFieldRulesBrowseGroups.value.map((group) => group.vin).join('\u0000'),
  ],
  () => reconcileSelectedFeedFieldRulesVin(),
  { immediate: true, flush: 'post' },
)

/**
 * Browser-row click prefills the VIN form and runs the lookup. The
 * in-flight guard (Adjustment #2) is enforced at BOTH layers — the
 * row CSS is muted and `submitVinLookup` itself is a no-op while the
 * VIN trail is loading — so a fast double-click can never stack
 * duplicate fetches.
 */
async function onBrowserRowClick(vin: string) {
  if (vinLookupInFlight.value) return
  const trimmed = String(vin || '').trim()
  if (!trimmed) return
  vinInput.value = trimmed
  await submitVinLookup()
}

/**
 * Whether a "Load more" affordance is available. In fieldMapping search mode
 * paging follows the SEARCH cursor; otherwise it follows the store's browse
 * cursor (unchanged for every other stage).
 */
const canLoadMore = computed(() => {
  if (decisionSearchActive.value) return Boolean(decisionSearchCursor.value)
  // An exact-VIN feed field rule search resolves a single VIN; paging more
  // browse pages behind it would be confusing, so hide "Load more" until the
  // search is cleared.
  if (feedFieldRulesSearchActive.value) return false
  return store.activeBrowserHasMore
})

const loadMoreLoading = computed(() =>
  decisionSearchActive.value
    ? decisionSearchStatus.value === 'loading'
    : activeBrowserStatus.value === 'loading',
)

async function onLoadMore() {
  if (decisionSearchActive.value) {
    await loadMoreDecisionSearch()
    return
  }
  const stage = store.activeBrowserStage
  if (!stage) return
  if (activeBrowserStatus.value === 'loading') return
  await store.loadNextPage(stage, {
    endpoint: props.endpoint,
    jazelId: props.jazelId,
    correlationId: props.correlationId,
    sessionToken: props.sessionToken,
  })
}

// -----------------------------------------------------------------------------
// Debounced server-side search driver. fieldMapping (VIN/field) and groups
// (VIN/group) participate; other stage-scoped browses keep their pure
// client-side VIN filter (`visibleBrowserRowViewModels`). Clearing the box
// returns to browse mode.
// -----------------------------------------------------------------------------
watch(
  () => stageVinSearch.value,
  (next) => {
    if (!isSearchableDecisionBrowse.value) return
    if (decisionSearchDebounce) {
      clearTimeout(decisionSearchDebounce)
      decisionSearchDebounce = undefined
    }
    const term = String(next ?? '').trim()
    if (term.length === 0) {
      resetDecisionSearch()
      return
    }
    decisionSearchDebounce = setTimeout(() => {
      decisionSearchDebounce = undefined
      void runDecisionSearch(term)
    }, DECISION_SEARCH_DEBOUNCE_MS)
  },
)

// -----------------------------------------------------------------------------
// Feed Field Rules VIN search driver. Unlike the `?search=` stages, feed field
// rule records are located by exact VIN via the cross-stage VIN trail. A term
// that already matches a loaded VIN is filtered client-side (no network); a
// term that does not is resolved (debounced) against the trail endpoint and
// merged into the list. Clearing the box returns to browse mode.
// -----------------------------------------------------------------------------
watch(
  () => stageVinSearch.value,
  (next) => {
    if (!isFeedFieldRulesDecisionBrowse.value) return
    if (feedFieldRulesSearchDebounce) {
      clearTimeout(feedFieldRulesSearchDebounce)
      feedFieldRulesSearchDebounce = undefined
    }
    const term = String(next ?? '').trim()
    if (term.length === 0) {
      resetFeedFieldRulesSearch()
      return
    }
    // A loaded VIN already matches → pure client filter, drop any stale trail.
    if (feedFieldRulesLoadedHasMatch(term)) {
      feedFieldRulesSearchSeq += 1
      feedFieldRulesSearchStatus.value = 'idle'
      feedFieldRulesTrailRecords.value = []
      return
    }
    feedFieldRulesSearchStatus.value = 'loading'
    feedFieldRulesSearchDebounce = setTimeout(() => {
      feedFieldRulesSearchDebounce = undefined
      void runFeedFieldRulesVinSearch(term)
    }, FEED_FIELD_RULES_SEARCH_DEBOUNCE_MS)
  },
)

// Reset search whenever the active stage changes or the dialog closes so a
// term never leaks across stages / reopens.
watch(
  [() => store.activeBrowserStage, () => store.dialogOpen],
  () => {
    stageVinSearch.value = ''
    resetDecisionSearch()
    resetFeedFieldRulesSearch()
  },
)

onBeforeUnmount(() => {
  if (decisionSearchDebounce) clearTimeout(decisionSearchDebounce)
  if (feedFieldRulesSearchDebounce) clearTimeout(feedFieldRulesSearchDebounce)
})

// =============================================================================
// Phase 7.y — Watchers
//
// Bundled into a single watcher (same shape as Phase 6.z) so the
// "two separate watchers race on the same state" footgun cannot
// recur. Two responsibilities:
//
//   1. When the dialog opens AND no active stage is set, resolve the
//      default tab via the store's getter (Guardrail #1: persisted
//      selection wins). When the active stage IS set (initial open
//      OR after a tab switch), call `loadFirstPage(stage)`. The
//      store-side guard (Guardrail #2) makes the call a no-op when
//      the slice is non-empty or already loading, so this watcher
//      can safely fire on every dialog-open + every active-stage
//      change without stacking duplicate fetches.
//
//   2. When the dialog is open AND `pendingLookupNonce` changes from
//      the local last-seen value, consume the pending VIN, prefill
//      the input, and submit the lookup. The nonce — not the VIN —
//      is the source of truth for "this is a fresh row click", so
//      clicking the same row repeatedly still re-runs the lookup
//      (subject to the in-flight guard).
// =============================================================================

const lastSeenNonce = ref(0)

watch(
  () => store.browserStageScope,
  (stage) => {
    if (stage === 'preProcessor') {
      preProcessorDecisionFilter.value = resolveDefaultPreProcessorTab()
    }
  },
)

watch(
  [() => store.dialogOpen, () => store.activeBrowserStage, () => store.browserStageScope, () => props.correlationId, () => store.pendingLookupNonce],
  async ([isOpen, _activeStage, _scope, _correlationId, nonce]) => {
    if (!isOpen) return
    // Panel modes (pass-through) own their own panel + fetches; the stage
    // browser and cross-stage VIN lookup are inert.
    if (isPanelModeDialog.value) return

    // 1) Resolve / load the active stage's first page.
    if (!store.activeBrowserStage) {
      const next = store.resolveDefaultBrowserStage()
      if (next) store.setActiveBrowserStage(next)
    }
    const currentStage = store.activeBrowserStage
    if (currentStage) {
      void store.loadFirstPage(currentStage, {
        endpoint: props.endpoint,
        jazelId: props.jazelId,
        correlationId: props.correlationId,
        sessionToken: props.sessionToken,
      })
    }

    // Phase 3 — when the listingRules section is active, also load the
    // publish-wide blocked-decisions drill-down (idempotent store-side).
    if (currentStage === 'listingRules') {
      void store.loadBlockedDecisionsFirstPage({
        endpoint: props.endpoint,
        jazelId: props.jazelId,
        correlationId: props.correlationId,
        sessionToken: props.sessionToken,
      })
    }

    // 2) Consume any pending row-action handoff. Compare against the
    //    LOCAL last-seen value so visibility-only toggles (open with
    //    nothing pending) don't accidentally re-run a lookup.
    if (typeof nonce === 'number' && nonce !== lastSeenNonce.value) {
      lastSeenNonce.value = nonce
      const pendingVin = store.consumePendingLookup()
      if (pendingVin.length > 0) {
        vinInput.value = pendingVin
        await submitVinLookup()
      }
    }
  },
  { immediate: true, flush: 'post' },
)

// =============================================================================
// Relative time formatter (subtitle helper).
//
// Copied verbatim from the Preview dialog rather than extracted into a
// shared util — Phase 6.y deliberately keeps the diff small. A later
// cleanup PR will consolidate when both dialogs are stable.
// =============================================================================

function formatRelativeTime(isoString: string): string {
  const ts = Date.parse(isoString)
  if (Number.isNaN(ts)) return 'recently'
  const diffMs = Date.now() - ts
  if (diffMs < 0) return 'just now'
  const minutes = Math.floor(diffMs / 60_000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes} min ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours} hr ago`
  try {
    return new Date(ts).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  } catch {
    return new Date(ts).toISOString().slice(0, 10)
  }
}
</script>

<style scoped>
.detailed-publish-impact-card {
  max-height: 82vh;
}

.detailed-publish-impact-body {
  overflow: hidden;
}

.detailed-publish-impact-results {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.detailed-publish-impact-results-scroll {
  max-height: 420px;
  overflow-y: auto;
  border-radius: 4px;
}

.v-list-item {
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.v-list-item:last-child {
  border-bottom: none;
}
.vin-cell {
  font-family: 'JetBrains Mono', 'SFMono-Regular', Menlo, Monaco, Consolas, 'Liberation Mono',
    'Courier New', monospace;
  font-size: 12px;
}
.field-mapping-decision-master-detail {
  display: flex;
  gap: 16px;
  align-items: flex-start;
}
.field-mapping-vin-list {
  flex: 0 0 260px;
  max-height: min(420px, 50vh);
  overflow-y: auto;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
}
.field-mapping-vin-list-row {
  appearance: none;
  background: transparent;
  border: 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  color: inherit;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 10px 12px;
  text-align: left;
  width: 100%;
}
.field-mapping-vin-list-row:last-child {
  border-bottom: 0;
}
.field-mapping-vin-list-row:hover,
.field-mapping-vin-list-row--selected {
  background-color: rgba(0, 0, 0, 0.04);
}
.field-mapping-vin-list-row--selected .vin-cell {
  font-weight: 600;
}
.field-mapping-details-panel {
  flex: 1 1 auto;
  min-width: 0;
  position: sticky;
  top: 0;
  align-self: flex-start;
  max-height: min(420px, 50vh);
  overflow-y: auto;
}
@media (max-width: 700px) {
  .field-mapping-decision-master-detail {
    flex-direction: column;
  }

  .field-mapping-vin-list,
  .field-mapping-details-panel {
    flex: 1 1 auto;
    max-height: none;
    position: static;
    width: 100%;
  }
}
.field-mapping-details-header {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
  margin-bottom: 10px;
}
.field-mapping-change-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.field-mapping-change-card {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 4px;
  padding: 12px;
  background-color: rgba(0, 0, 0, 0.02);
  overflow-wrap: anywhere;
}
.field-mapping-change-lines {
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin-top: 6px;
}
.field-mapping-change-diff {
  overflow-wrap: anywhere;
}
.field-mapping-source-line {
  line-height: 1.35;
}
.feed-field-rules-matched-rule {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: 2px;
}
/* Vehicle Groups master/detail badges + sections (reuse the master/detail
   layout above; only the group-specific chrome is defined here). */
.groups-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
}
.groups-badge {
  display: inline-flex;
  align-items: center;
  border-radius: 10px;
  padding: 1px 8px;
  font-size: 11px;
  line-height: 1.5;
  background-color: rgba(25, 118, 210, 0.1);
  color: #1565c0;
  white-space: nowrap;
}
.groups-badge--previous {
  background-color: rgba(0, 0, 0, 0.06);
  color: rgba(0, 0, 0, 0.7);
}
/* Website Visibility (listingRules) — run summary + decision badges. */
.visibility-summary {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 4px;
  padding: 12px;
  background-color: rgba(0, 0, 0, 0.02);
}
.visibility-badge {
  display: inline-flex;
  align-items: center;
  border-radius: 10px;
  padding: 1px 8px;
  font-size: 11px;
  line-height: 1.5;
  white-space: nowrap;
}
.visibility-badge--blocked {
  background-color: rgba(211, 47, 47, 0.1);
  color: #c62828;
}
.visibility-badge--accepted {
  background-color: rgba(46, 125, 50, 0.12);
  color: #2e7d32;
}
/* Phase 3 — Blocked vehicles drill-down panel. */
.blocked-decisions-panel {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  padding: 12px;
}
.blocked-decision-row {
  padding: 8px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.blocked-decision-row:last-child {
  border-bottom: 0;
}
.blocked-decision-row__head {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 2px;
}
.blocked-decision-chip {
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  padding: 3px 8px;
  border-radius: 10px;
  background-color: rgba(0, 0, 0, 0.06);
  color: rgba(0, 0, 0, 0.7);
}
.blocked-decision-chip--authoritative {
  background-color: rgba(198, 40, 40, 0.12);
  color: #c62828;
}
.blocked-decision-chip--recovered {
  background-color: rgba(239, 108, 0, 0.12);
  color: #ef6c00;
}
.blocked-decision-chip--unavailable {
  background-color: rgba(0, 0, 0, 0.08);
  color: rgba(0, 0, 0, 0.6);
}
.blocked-decision-chip--classification {
  background-color: rgba(21, 101, 192, 0.1);
  color: #1565c0;
}
.groups-detail-section {
  margin-bottom: 12px;
}
.groups-detail-section:last-child {
  margin-bottom: 0;
}
.groups-reason-list {
  list-style: disc inside;
  margin: 0;
  padding: 0;
  font-size: 13px;
}
.groups-reason-list > li + li {
  margin-top: 4px;
}
.groups-reason-explanation {
  display: inline;
}
.groups-reason-criterion {
  /* Indent the captured criterion under its explanation bullet. */
  margin-left: 18px;
  margin-top: 1px;
}
.groups-reason-note {
  font-style: italic;
}
/* Vehicle Groups master/detail — left list scrolls independently so the
   right details pane stays visible while browsing VINs. */
.groups-decision-layout {
  display: flex;
  gap: 16px;
  align-items: flex-start;
}
.groups-vin-list {
  flex: 0 0 260px;
  max-height: min(420px, 50vh);
  overflow-y: auto;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
}
.groups-details-pane {
  flex: 1 1 auto;
  min-width: 0;
  position: sticky;
  top: 0;
  align-self: flex-start;
  max-height: min(420px, 50vh);
  overflow-y: auto;
}
.reason-cell {
  max-width: 360px;
}
.reason-cell--preprocessor {
  max-width: none;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
  line-height: 1.375;
}
.reason-lines {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.reason-summary {
  font-weight: 500;
  color: rgba(var(--v-theme-on-surface), 0.82);
}
.reason-line {
  line-height: 1.375;
  overflow-wrap: anywhere;
  word-break: break-word;
  color: rgba(var(--v-theme-on-surface), 0.78);
}
/* Adjustment #2 — visual cue that VIN row clicks are blocked while a
   VIN lookup is in flight. The actual click guard lives in the
   handler so this stays purely cosmetic. */
.vin-row-disabled {
  opacity: 0.6;
  pointer-events: none;
}
tbody tr {
  cursor: pointer;
}
tbody tr:hover {
  background-color: rgba(0, 0, 0, 0.02);
}
</style>
